/**
 * Data Validator
 * ==============
 * Inventory reconciliation tool for Cin7 vs WCCS variance detection:
 * 1. Show total variance at a glance (Cin7 OUT vs WCCS Shipped)
 * 2. Weekly rollup showing WHERE variance concentrates
 * 3. Flagged transactions table with VARIANCE quantities (not transaction totals)
 *
 * Design: Dark monochrome, data-dense, no frills
 * - bg-neutral-950 background
 * - text-neutral-100 primary text
 * - border-neutral-800 borders
 * - text-emerald-300 for balanced (no variance)
 * - text-rose-300 for any variance
 * - font-mono for all numbers
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FileSpreadsheet,
  CheckCircle2,
  Upload,
  Loader2,
  RefreshCw,
  Download,
  ChevronDown,
  ChevronRight,
  Send,
  Plus,
  Trash2,
  X,
  Clock,
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || `${window.location.origin}/api`;

// Types for reconciliation report
interface FlaggedTransaction {
  sku: string;
  product: string;
  transaction: string;
  reference: string;
  quantity: number; // This is VARIANCE
  week: string;
  weekKey: string | null;
  explanation: string;
  status: 'VALIDATED' | 'NOT_FOUND' | 'AUTO_DETECTED' | 'USER_ADDED' | 'ACCEPTED' | 'IGNORED';
  source?: 'known' | 'auto' | 'user' | 'reference';
  id?: string; // For tracking
  userStatus?: 'pending' | 'accepted' | 'ignored'; // User action on this finding
}

interface WeekComparison {
  weekKey: string;
  weekLabel: string;
  cin7Total: number;
  wccsTotal: number;
  variance: number;
  status: string;
  cin7Transactions: any[];
  wccsTransactions: any[];
}

interface AdditionalFinding {
  week: string;
  weekKey: string;
  variance: number;
  cin7Total: number;
  wccsTotal: number;
  possibleCause: string;
}

interface ReferenceMatch {
  reference: string;
  sku: string;
  cin7Qty: number | null;
  cin7Week: string | null;
  cin7WeekKey: string | null;
  externalQty: number | null;
  externalWeek: string | null;
  externalWeekKey: string | null;
  matchStatus: 'MATCHED' | 'CIN7_ONLY' | 'EXTERNAL_ONLY' | 'QUANTITY_MISMATCH' | 'PROBABLE_MATCH' | 'FLOW_THROUGH' | 'DATE_GAP';
  variance: number;
  timingGap?: {
    daysDifference: number;
    weeksDifference: number;
    isWithinTolerance: boolean;
  } | null;
}

interface ReferenceMatchingSummary {
  totalReferences: number;
  matchedCount: number;
  qtyMismatchCount: number;
  cin7OnlyCount: number;
  externalOnlyCount: number;
  timingLagCount: number;
  netVariance: number;
  probableMatchCount?: number;
}

interface ExpectedVariance {
  dateGapVariance: number;
  dateGapTransactions: number;
  cin7OutsideOverlap: number;
  externalOutsideOverlap: number;
  overlapPeriod?: {
    start: string;
    end: string;
  } | null;
  cin7DateRange?: { start: string; end: string };
  externalDateRange?: { start: string; end: string };
}

interface DeepAnalysis {
  rootCause?: string | null;
  investigateFirst?: { week: string; reason: string } | null;
  nextSteps?: string[];
  generatedAt?: string;
  error?: string;
  content?: string | null;
  model?: string;
}

// Pre-reconciliation audit types
interface AuditWarning {
  code: string;
  severity: 'critical' | 'warning';
  message: string;
  details?: Record<string, any>;
  source?: string;
}

interface AuditRecommendation {
  priority: number;
  action: string;
  details: string;
}

interface DateRangeInfo {
  minDateStr?: string;
  maxDateStr?: string;
  dayCount?: number;
  transactionCount?: number;
  hasValidDates?: boolean;
  sourceName?: string;
}

interface DateRangeComparison {
  overlapDays?: number;
  overlapPercent?: number;
  overlapStart?: string;
  overlapEnd?: string;
  source1OnlyDays?: number;
  source2OnlyDays?: number;
  source1MissingPeriod?: { start: string; end: string; days: number };
  source2MissingPeriod?: { start: string; end: string; days: number };
  hasOverlap?: boolean;
}

interface AuditResult {
  success: boolean;
  completeness: {
    overallScore: number;
    source1TransactionCount: number;
    source2TransactionCount: number;
  };
  dateRanges: {
    source1: DateRangeInfo;
    source2: DateRangeInfo;
    comparison: DateRangeComparison;
  };
  warnings: AuditWarning[];
  recommendations: AuditRecommendation[];
  canProceed: boolean;
  requiresAttention: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hierarchy?: any;
}

interface VarianceBreakdown {
  totalVariance: number;
  explainableVariance: number;
  adjustedVariance: number;
  explainablePercent: number;
  explanation: string;
  components?: Array<{
    type: string;
    description: string;
    units: number;
    period: string;
  }>;
  overlapPeriod?: {
    start: string;
    end: string;
    days: number;
  };
}

interface SkuBreakdownItem {
  sku: string;
  productName: string;
  cin7TotalOut: number;
  externalTotalShipped: number;
  variance: number;
  transactionCounts: { cin7: number; wccs: number };
  refSummary: ReferenceMatchingSummary | null;
}

interface ReconciliationReport {
  success: boolean;
  timestamp: string;
  reportId?: string;
  sku?: string;
  summary: {
    sku: string | null;
    productName: string;
    dateRange: { start: string | null; end: string | null };
    cin7TotalOut: number;
    wccsTotalShipped: number;
    totalVariance: number;
    totalAbsoluteVariance?: number;
    varianceDirection: string;
    transactionCounts: { cin7: number; wccs: number };
    externalLabel?: string;
    grossAbsoluteVariance?: number;
    grossPositiveVariance?: number;
    grossNegativeVariance?: number;
  };
  flaggedTransactions: FlaggedTransaction[];
  weeklyComparison: WeekComparison[];
  additionalFindings: AdditionalFinding[];
  reconciliation: {
    explainedVariance: number;
    unexplainedVariance: number;
    percentExplained: number;
  };
  deepAnalysis?: DeepAnalysis;
  varianceBreakdown?: VarianceBreakdown;
  auditResult?: AuditResult;
  expectedVariance?: ExpectedVariance;
  referenceMatching?: {
    matches: ReferenceMatch[];
    summary: ReferenceMatchingSummary;
    transactionsWithoutRef?: {
      cin7Count: number;
      externalCount: number;
    };
  };
  scope: {
    cin7SkuCount: number;
    wccsSkuCount: number;
    analyzedSku: string | null;
    note: string;
  };
  skuBreakdown?: SkuBreakdownItem[];
  skuResults?: ReconciliationReport[];
  appliedConfig?: {
    locationFiltered?: string;
    locationOriginalCount?: number;
    [key: string]: any;
  };
}

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  visual?: ChatVisual;
}

interface ChatVisual {
  type: 'data_table' | 'metric_card' | 'list_card' | 'chart_card';
  title?: string;
  columns?: string[];
  rows?: string[][];
  value?: string;
  subtitle?: string;
  items?: Array<{ label: string; value?: string }>;
}

// Multi-source types
interface WarehouseFile {
  id: string;
  name: string;
  file: File | null;
}

interface WarehouseComparison {
  warehouseId: string;
  warehouseName: string;
  summary: {
    warehouseTotalShipped: number;
    cin7TotalOut: number;
    variance: number;
    varianceDirection: string;
    skuCount: number;
    transactionCounts: { cin7: number; warehouse: number };
  };
  weeklyComparison: WeekComparison[];
  flaggedTransactions: FlaggedTransaction[];
  additionalFindings: AdditionalFinding[];
  reconciliation: {
    explainedVariance: number;
    unexplainedVariance: number;
    percentExplained: number;
  };
}

interface SkuBreakdown {
  sku: string;
  productName: string;
  cin7Out: number;
  warehouseShipped: Record<string, number>;
  totalWarehouseShipped: number;
  variance: number;
  // Coverage tracking
  hasWarehouseData?: boolean;
  coverageStatus?: 'tracked' | 'missing';
}

interface AggregatedVarianceBreakdown {
  total: number;
  reconcilable: number;
  missingCoverage: number;
  trackedSkuCount: number;
  missingSkuCount: number;
}

interface MultiSourceReport {
  success: boolean;
  reportId: string;
  timestamp: string;
  master: {
    source: string;
    totalOut: number;
    skuCount: number;
    transactionCount: number;
    dateRange: { start: string | null; end: string | null };
  };
  warehouseComparisons: WarehouseComparison[];
  aggregated: {
    totalWarehouseShipped: number;
    totalCin7Out: number;
    totalVariance: number;
    varianceDirection: string;
    warehouseCount: number;
    skuBreakdown: SkuBreakdown[];
    weeklyBreakdown: Array<{
      weekKey: string;
      weekLabel: string;
      cin7Total: number;
      warehouseTotals: Record<string, number>;
      combinedWarehouseTotal: number;
      variance: number;
      status: string;
    }>;
    varianceBreakdown?: AggregatedVarianceBreakdown;
  };
  reconciliation: {
    totalExplainedVariance: number;
    totalUnexplainedVariance: number;
    overallPercentExplained: number;
  };
  deepAnalysis?: DeepAnalysis;
}

interface RecentReport {
  report_id: string;
  mode: 'single' | 'multi-file';
  summary_cache: {
    productName: string | null;
    sku: string | null;
    totalVariance: number;
    varianceDirection: string;
    dateRange: { start: string | null; end: string | null } | null;
    externalLabel: string;
    flaggedCount: number;
    fileNames?: string[];
  };
  created_at: string;
}

interface DataValidatorProps {
  onBack?: () => void;
  getAuthHeaders?: () => Record<string, string>;
}

const STORAGE_KEY = 'inventory-reconciliation-report';

/**
 * Extract a clean, readable warehouse name from a filename.
 * Examples:
 *   "Centigrade Transaction Report.xls" -> "Centigrade"
 *   "WCCS BBQ Pork Bao Buns.xls" -> "WCCS"
 *   "West Coast Cold Storage Report.xlsx" -> "West Coast Cold Storage"
 *   "external_warehouse_data.csv" -> "External Warehouse"
 */
function extractWarehouseName(filename: string | undefined): string {
  if (!filename) return 'External';

  // Remove file extension
  const nameWithoutExt = filename.replace(/\.(xlsx?|csv)$/i, '');

  // Common warehouse identifiers to look for
  const warehousePatterns = [
    { pattern: /centigrade/i, name: 'Centigrade' },
    { pattern: /wccs|west\s*coast/i, name: 'WCCS' },
    { pattern: /3pl/i, name: '3PL' },
  ];

  for (const { pattern, name } of warehousePatterns) {
    if (pattern.test(nameWithoutExt)) {
      return name;
    }
  }

  // If no pattern matched, try to extract first meaningful word(s)
  // Remove common suffixes like "Report", "Transaction", "Data", etc.
  const cleaned = nameWithoutExt
    .replace(/[-_]/g, ' ')
    .replace(/\s+(report|transaction|data|export|inventory|movement|details?)\s*/gi, ' ')
    .trim();

  // Take first 2-3 words max
  const words = cleaned.split(/\s+/).filter(w => w.length > 1);
  if (words.length > 0) {
    return words.slice(0, 3).join(' ');
  }

  return 'External';
}

// Clarification types
interface DataIssue {
  type: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  details?: Record<string, any>;
}

interface Clarification {
  id: string;
  question: string;
  type: 'select' | 'input';
  options?: { value: string; label: string; recommended?: boolean }[];
  impact?: string;
  skuList?: { sku: string; units: number }[];
}

interface ClarificationState {
  dataIssues: DataIssue[];
  clarifications: Clarification[];
  suggestedConfig: Record<string, any>;
  summary: {
    rawVariance: number;
    estimatedTrueVariance: number;
  };
}

export default function DataValidator({ getAuthHeaders }: DataValidatorProps) {
  // Call fresh each request to pick up token refreshes
  const authHeaders = getAuthHeaders ? getAuthHeaders() : {};
  const [phase, setPhase] = useState<'upload' | 'processing' | 'audit' | 'clarification' | 'report'>('upload');
  const [cin7File, setCin7File] = useState<File | null>(null);
  const [wccsFile, setWccsFile] = useState<File | null>(null);
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());

  // Audit phase state
  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);

  // Clarification phase state
  const [clarificationState, setClarificationState] = useState<ClarificationState | null>(null);
  const [clarificationAnswers, setClarificationAnswers] = useState<Record<string, string>>({});

  // Multi-source mode state
  const [mode, setMode] = useState<'single' | 'multi-file' | 'multi-warehouse'>('single');
  const [masterFile, setMasterFile] = useState<File | null>(null);
  const [warehouseFiles, setWarehouseFiles] = useState<WarehouseFile[]>([
    { id: 'wh-1', name: 'Warehouse 1', file: null }
  ]);
  const [multiReport, setMultiReport] = useState<MultiSourceReport | null>(null);
  const [activeTab, setActiveTab] = useState<string>('aggregated');

  // Multi-file mode state (multiple internal + multiple external)
  const [internalFiles, setInternalFiles] = useState<File[]>([]);
  const [externalFiles, setExternalFiles] = useState<File[]>([]);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // User findings state
  const [userFindings, setUserFindings] = useState<FlaggedTransaction[]>([]);
  const [findingStatuses, setFindingStatuses] = useState<Record<string, 'pending' | 'accepted' | 'ignored'>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFinding, setNewFinding] = useState({
    transaction: '',
    reference: '',
    quantity: '',
    week: '',
    explanation: ''
  });

  // Export dropdown state
  const [exportOpen, setExportOpen] = useState(false);

  // Reference breakdown filter state
  const [refFilter, setRefFilter] = useState<string>('with_variance');

  // Weekly breakdown collapsed state (collapsed by default when reference matching exists)
  const [weeklyCollapsed, setWeeklyCollapsed] = useState(true);

  // Recent reports state
  const [recentReports, setRecentReports] = useState<RecentReport[]>([]);
  const [recentReportsLoading, setRecentReportsLoading] = useState(false);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);

  // Conversation save debounce ref
  const conversationSaveTimeout = useRef<NodeJS.Timeout | null>(null);

  // Per-SKU selector state (null = aggregate view)
  const [selectedSku, setSelectedSku] = useState<string | null>(null);

  // Derive dynamic warehouse label from report (backend), file name, or audit result
  const externalLabel = report?.summary?.externalLabel
    || (wccsFile ? extractWarehouseName(wccsFile.name) : null)
    || (externalFiles.length > 0 ? extractWarehouseName(externalFiles[0].name) : null)
    || auditResult?.dateRanges?.source2?.sourceName
    || 'External';

  // Load persisted report on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.report && parsed.messages) {
          setReport(parsed.report);
          setMessages(parsed.messages);
          if (parsed.userFindings) {
            setUserFindings(parsed.userFindings);
          }
          if (parsed.findingStatuses) {
            setFindingStatuses(parsed.findingStatuses);
          }
          setPhase('report');
        }
      }
    } catch (e) {
      console.error('Failed to load stored report:', e);
    }
  }, []);

  // Persist report, messages, user findings and statuses when they change
  useEffect(() => {
    if (report) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ report, messages, userFindings, findingStatuses }));
      } catch (e) {
        console.error('Failed to save report:', e);
      }
    }
  }, [report, messages, userFindings, findingStatuses]);

  useEffect(() => {
    // Scroll only within the chat container, not the whole page
    const el = chatEndRef.current;
    if (el?.parentElement) {
      el.parentElement.scrollTop = el.parentElement.scrollHeight;
    }
  }, [messages]);

  // Fetch recent reports when on upload phase
  const fetchRecentReports = useCallback(async () => {
    setRecentReportsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/data-validation/reports`, { headers: { ...authHeaders } });
      const data = await response.json();
      if (data.success && data.reports) {
        setRecentReports(data.reports);
      }
    } catch (err) {
      console.error('Failed to fetch recent reports:', err);
    } finally {
      setRecentReportsLoading(false);
    }
  }, []);

  // Fetch recent reports on mount and when returning to upload phase
  useEffect(() => {
    if (phase === 'upload') {
      fetchRecentReports();
    }
  }, [phase, fetchRecentReports]);

  // Load a saved report
  const handleLoadReport = async (reportId: string) => {
    setLoadingReportId(reportId);
    try {
      const response = await fetch(`${API_BASE}/data-validation/report/${reportId}`, { headers: { ...authHeaders } });
      const data = await response.json();

      if (data.success && data.report) {
        setReport(data.report);
        setPhase('report');
        setSelectedSku(null);

        // Restore conversation history if available
        if (data.conversationHistory && data.conversationHistory.length > 0) {
          setMessages(data.conversationHistory);
        } else {
          // Create initial message
          const variance = data.report.summary?.totalVariance || 0;
          const direction = variance > 0 ? 'more in Cin7' : variance < 0 ? 'more in external' : 'balanced';
          setMessages([{
            id: Date.now(),
            role: 'assistant',
            content: `Loaded report for ${data.report.summary?.productName || 'inventory'}. Variance: ${variance > 0 ? '+' : ''}${variance.toLocaleString()} units (${direction}).`
          }]);
        }
      }
    } catch (err) {
      console.error('Failed to load report:', err);
    } finally {
      setLoadingReportId(null);
    }
  };

  // Save conversation to backend (debounced)
  const saveConversation = useCallback(async (reportId: string, history: ChatMessage[]) => {
    try {
      await fetch(`${API_BASE}/data-validation/reports/${reportId}/conversation`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ conversationHistory: history })
      });
    } catch (err) {
      console.error('Failed to save conversation:', err);
    }
  }, []);

  // Debounce conversation save after messages change
  useEffect(() => {
    if (report?.reportId && messages.length > 1) {
      if (conversationSaveTimeout.current) {
        clearTimeout(conversationSaveTimeout.current);
      }
      conversationSaveTimeout.current = setTimeout(() => {
        saveConversation(report.reportId!, messages);
      }, 2000); // Save 2 seconds after last message
    }
    return () => {
      if (conversationSaveTimeout.current) {
        clearTimeout(conversationSaveTimeout.current);
      }
    };
  }, [messages, report?.reportId, saveConversation]);

  const handleReconcile = async (bypassAudit: boolean = false) => {
    if (!cin7File || !wccsFile) {
      setError('Upload both files');
      return;
    }

    setPhase('processing');
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('internal', cin7File);
      formData.append('external', wccsFile);
      if (bypassAudit) {
        formData.append('bypassAudit', 'true');
      }

      const response = await fetch(`${API_BASE}/data-validation/reconcile`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: formData
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Reconciliation failed');
      }

      // Check if we got an audit-only response
      if (data.phase === 'audit' && data.auditResult) {
        setAuditResult(data.auditResult);
        setPhase('audit');
        return;
      }

      // Check if we got a clarification response
      if (data.phase === 'clarification' && data.clarifications) {
        setClarificationState({
          dataIssues: data.dataIssues || [],
          clarifications: data.clarifications || [],
          suggestedConfig: data.suggestedConfig || {},
          summary: data.summary || { rawVariance: 0, estimatedTrueVariance: 0 }
        });
        setAuditResult(data.auditResult || null);
        // Pre-fill with recommended answers
        const defaultAnswers: Record<string, string> = {};
        data.clarifications.forEach((c: Clarification) => {
          const recommended = c.options?.find((o: { value: string; label: string; recommended?: boolean }) => o.recommended);
          if (recommended) {
            defaultAnswers[c.id] = recommended.value;
          }
        });
        setClarificationAnswers(defaultAnswers);
        setPhase('clarification');
        return;
      }

      setReport(data);
      setAuditResult(data.auditResult || null);
      setPhase('report');
      setUserFindings([]); // Clear user findings for new report
      setFindingStatuses({}); // Clear statuses for new report
      setSelectedSku(null);

      // Build initial chat message with variance breakdown context
      const variance = data.summary.totalVariance;
      const direction = variance > 0 ? 'more in Cin7' : variance < 0 ? `more in ${externalLabel}` : 'balanced';
      let initialMessage = `Analyzed ${data.summary.productName || 'inventory'}. Total variance: ${variance > 0 ? '+' : ''}${variance.toLocaleString()} units (${direction}).`;

      if (data.varianceBreakdown?.explainablePercent >= 50) {
        initialMessage += ` ${data.varianceBreakdown.explainablePercent}% of variance is explained by date range mismatch. Only ${data.varianceBreakdown.adjustedVariance.toLocaleString()} units require investigation.`;
      } else {
        initialMessage += ` ${data.flaggedTransactions.length} issues found. Ask me about specific weeks or transactions.`;
      }

      setMessages([{
        id: Date.now(),
        role: 'assistant',
        content: initialMessage
      }]);

    } catch (err: any) {
      setError(err.message || 'Failed to process files');
      setPhase('upload');
    } finally {
      setLoading(false);
    }
  };

  // Handle proceeding from audit phase (detects which mode was used)
  const handleProceedFromAudit = () => {
    if (mode === 'multi-file') {
      handleMultiFileReconcile(true);
    } else {
      handleReconcile(true);
    }
  };

  // Handle re-upload from audit phase
  const handleReUploadFromAudit = () => {
    setPhase('upload');
    setAuditResult(null);
  };

  // Handle clarification submission
  const handleClarificationSubmit = async () => {
    if (!cin7File || !wccsFile) {
      setError('Files not available');
      return;
    }

    setPhase('processing');
    setLoading(true);
    setError(null);

    try {
      // Build config from answers
      const config: Record<string, any> = { ...clarificationState?.suggestedConfig };

      // Map answers to config
      Object.entries(clarificationAnswers).forEach(([id, value]) => {
        if (id === 'uom_conversion' && value !== 'custom') {
          config.uomConversionFactor = parseFloat(value);
        } else if (id === 'date_range') {
          config.dateRangeHandling = value;
        } else if (id === 'unmapped_skus') {
          config.unmappedSkuHandling = value;
        } else if (id === 'location_filter') {
          config.locationFilter = value;
        }
      });

      const formData = new FormData();
      formData.append('internal', cin7File);
      formData.append('external', wccsFile);
      formData.append('reconciliationConfig', JSON.stringify(config));
      formData.append('bypassClarifications', 'true');

      const response = await fetch(`${API_BASE}/data-validation/reconcile`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: formData
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Reconciliation failed');
      }

      setReport(data);
      setAuditResult(data.auditResult || null);
      setPhase('report');
      setUserFindings([]);
      setFindingStatuses({});
      setSelectedSku(null);

      // Build initial chat message
      const variance = data.summary.totalVariance;
      const direction = variance > 0 ? 'more in Cin7' : variance < 0 ? `more in ${externalLabel}` : 'balanced';
      setMessages([{
        id: Date.now(),
        role: 'assistant',
        content: `Reconciliation complete with your configuration. Variance: ${variance > 0 ? '+' : ''}${variance.toLocaleString()} units (${direction}). ${data.appliedConfig?.locationFiltered ? `Filtered to ${data.appliedConfig.locationFiltered}. ` : ''}${data.appliedConfig?.uomConversionApplied ? `Unit conversion (${data.appliedConfig.uomConversionApplied}x) applied. ` : ''}${data.appliedConfig?.excludedSkus ? `${data.appliedConfig.excludedSkus} unmapped SKUs excluded. ` : ''}${data.flaggedTransactions.length} issues to review.`
      }]);

    } catch (err) {
      console.error('Reconciliation failed:', err);
      setError(err instanceof Error ? err.message : 'Reconciliation failed');
      setPhase('clarification');
    } finally {
      setLoading(false);
    }
  };

  // Multi-source reconciliation
  const handleMultiReconcile = async () => {
    if (!masterFile) {
      setError('Upload master (Cin7) file');
      return;
    }

    const validWarehouses = warehouseFiles.filter(wh => wh.file);
    if (validWarehouses.length === 0) {
      setError('Upload at least one warehouse file');
      return;
    }

    setPhase('processing');
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('master', masterFile);

      // Add warehouse files and names
      const warehouseNames: string[] = [];
      validWarehouses.forEach(wh => {
        if (wh.file) {
          formData.append('warehouses', wh.file);
          warehouseNames.push(wh.name);
        }
      });
      formData.append('warehouseNames', JSON.stringify(warehouseNames));

      const response = await fetch(`${API_BASE}/data-validation/multi-reconcile`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: formData
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Multi-source reconciliation failed');
      }

      setMultiReport(data);
      setPhase('report');
      setActiveTab('aggregated');
      setUserFindings([]);
      setFindingStatuses({});

      // Initial chat message
      const variance = data.aggregated.totalVariance;
      const direction = variance > 0 ? 'more in Cin7' : variance < 0 ? 'more in warehouses' : 'balanced';
      setMessages([{
        id: Date.now(),
        role: 'assistant',
        content: `Analyzed ${data.master.skuCount} SKUs across ${data.warehouseComparisons.length} warehouse(s). Total variance: ${variance > 0 ? '+' : ''}${variance.toLocaleString()} units (${direction}). Click on warehouse tabs to see individual breakdowns.`
      }]);

    } catch (err: any) {
      setError(err.message || 'Failed to process files');
      setPhase('upload');
    } finally {
      setLoading(false);
    }
  };

  // Add warehouse file slot
  const addWarehouseSlot = () => {
    const newId = `wh-${Date.now()}`;
    setWarehouseFiles(prev => [...prev, { id: newId, name: `Warehouse ${prev.length + 1}`, file: null }]);
  };

  // Remove warehouse file slot
  const removeWarehouseSlot = (id: string) => {
    if (warehouseFiles.length <= 1) return;
    setWarehouseFiles(prev => prev.filter(wh => wh.id !== id));
  };

  // Update warehouse file
  const updateWarehouseFile = (id: string, file: File | null) => {
    setWarehouseFiles(prev => prev.map(wh =>
      wh.id === id ? { ...wh, file } : wh
    ));
  };

  // Update warehouse name
  const updateWarehouseName = (id: string, name: string) => {
    setWarehouseFiles(prev => prev.map(wh =>
      wh.id === id ? { ...wh, name } : wh
    ));
  };

  // Multi-file reconciliation (multiple internal + multiple external merged)
  const handleMultiFileReconcile = async (bypassAudit: boolean = false) => {
    if (internalFiles.length === 0) {
      setError('Upload at least one internal (Cin7) file');
      return;
    }
    if (externalFiles.length === 0) {
      setError('Upload at least one external (warehouse) file');
      return;
    }

    setPhase('processing');
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      // Add all internal files
      internalFiles.forEach(file => {
        formData.append('internal', file);
      });

      // Add all external files
      externalFiles.forEach(file => {
        formData.append('external', file);
      });

      if (bypassAudit) {
        formData.append('bypassAudit', 'true');
      }

      const response = await fetch(`${API_BASE}/data-validation/reconcile-merged`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: formData
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Reconciliation failed');
      }

      // Check if we got an audit-only response
      if (data.phase === 'audit' && data.auditResult) {
        setAuditResult(data.auditResult);
        setPhase('audit');
        return;
      }

      setReport(data);
      setAuditResult(data.auditResult || null);
      setPhase('report');
      setUserFindings([]);
      setFindingStatuses({});
      setSelectedSku(null);

      // Build initial chat message with variance breakdown context
      const variance = data.summary.totalVariance;
      const direction = variance > 0 ? 'more in Cin7' : variance < 0 ? `more in ${externalLabel}` : 'balanced';
      let initialMessage = `Analyzed ${internalFiles.length} internal files and ${externalFiles.length} external files. Total variance: ${variance > 0 ? '+' : ''}${variance.toLocaleString()} units (${direction}).`;

      if (data.varianceBreakdown?.explainablePercent >= 50) {
        initialMessage += ` ${data.varianceBreakdown.explainablePercent}% of variance is explained by date range mismatch. Only ${data.varianceBreakdown.adjustedVariance.toLocaleString()} units require investigation.`;
      } else {
        initialMessage += ` ${data.flaggedTransactions.length} issues found.`;
      }

      setMessages([{
        id: Date.now(),
        role: 'assistant',
        content: initialMessage
      }]);

    } catch (err: any) {
      setError(err.message || 'Failed to process files');
      setPhase('upload');
    } finally {
      setLoading(false);
    }
  };

  // Handle multi-file drop/select for internal files
  const handleInternalFilesChange = (files: FileList | null) => {
    if (!files) return;
    const validFiles = Array.from(files).filter(f =>
      f.name.endsWith('.xlsx') || f.name.endsWith('.xls')
    );
    setInternalFiles(prev => [...prev, ...validFiles]);
    // Clear cached report when new files added
    localStorage.removeItem(STORAGE_KEY);
    setReport(null);
    setPhase('upload');
  };

  // Handle multi-file drop/select for external files
  const handleExternalFilesChange = (files: FileList | null) => {
    if (!files) return;
    const validFiles = Array.from(files).filter(f =>
      f.name.endsWith('.xlsx') || f.name.endsWith('.xls')
    );
    setExternalFiles(prev => [...prev, ...validFiles]);
    // Clear cached report when new files added
    localStorage.removeItem(STORAGE_KEY);
    setReport(null);
    setPhase('upload');
  };

  // Remove file from internal list
  const removeInternalFile = (index: number) => {
    setInternalFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Remove file from external list
  const removeExternalFile = (index: number) => {
    setExternalFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Add user finding
  const handleAddFinding = () => {
    if (!newFinding.reference && !newFinding.transaction) return;
    if (!newFinding.quantity) return;

    const finding: FlaggedTransaction = {
      id: `user-${Date.now()}`,
      sku: report?.summary.sku || '',
      product: report?.summary.productName || '',
      transaction: newFinding.transaction || 'N/A',
      reference: newFinding.reference || 'N/A',
      quantity: parseInt(newFinding.quantity) || 0,
      week: newFinding.week || 'N/A',
      weekKey: null,
      explanation: newFinding.explanation || 'User-added finding',
      status: 'USER_ADDED',
      source: 'user'
    };

    setUserFindings(prev => [...prev, finding]);
    setNewFinding({ transaction: '', reference: '', quantity: '', week: '', explanation: '' });
    setShowAddForm(false);
  };

  // Delete user finding
  const handleDeleteFinding = (id: string) => {
    setUserFindings(prev => prev.filter(f => f.id !== id));
  };

  // Set finding status (accept/ignore)
  const handleSetFindingStatus = (findingKey: string, status: 'accepted' | 'ignored' | 'pending') => {
    setFindingStatuses(prev => ({
      ...prev,
      [findingKey]: status
    }));
  };

  // Generate a unique key for a finding
  const getFindingKey = (row: FlaggedTransaction, index: number) => {
    return row.id || `${row.reference}-${row.transaction}-${index}`;
  };

  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now(),
      role: 'user',
      content: chatInput.trim()
    };
    setMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await fetch(`${API_BASE}/data-validation/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          question: userMessage.content,
          reportId: report?.reportId,
          reportData: report,
          conversationHistory: messages.slice(-10)
        })
      });

      const data = await response.json();
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: data.answer || 'Unable to process.',
        visual: data.visual
      }]);
    } catch {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Connection error. Try again.'
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleReset = () => {
    setPhase('upload');
    setCin7File(null);
    setWccsFile(null);
    setReport(null);
    setMessages([]);
    setError(null);
    setExpandedWeeks(new Set());
    // Reset audit state
    setAuditResult(null);
    // Reset multi-source state
    setMode('single');
    setMasterFile(null);
    setWarehouseFiles([{ id: 'wh-1', name: 'Warehouse 1', file: null }]);
    setMultiReport(null);
    setActiveTab('aggregated');
    // Reset multi-file state
    setInternalFiles([]);
    setExternalFiles([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const toggleWeek = (weekKey: string) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(weekKey)) next.delete(weekKey);
      else next.add(weekKey);
      return next;
    });
  };

  const handleExport = () => {
    if (!report) return;

    const lines: string[] = [];
    lines.push('INVENTORY RECONCILIATION REPORT');
    lines.push(`Product,${report.summary.productName || 'N/A'}`);
    lines.push(`SKU,${report.summary.sku || 'Multiple'}`);
    lines.push(`Period,${report.summary.dateRange.start} to ${report.summary.dateRange.end}`);
    lines.push('');
    lines.push('SUMMARY');
    lines.push(`Cin7 OUT,${report.summary.cin7TotalOut}`);
    lines.push(`${externalLabel} Shipped,${report.summary.wccsTotalShipped}`);
    lines.push(`VARIANCE,${report.summary.totalVariance}`);
    lines.push('');
    lines.push('FLAGGED TRANSACTIONS');
    lines.push('SKU,Product,Transaction,Reference,Qty Gap,Week,Explanation,Status');
    report.flaggedTransactions.forEach(row => {
      lines.push(`${row.sku},${row.product},${row.transaction},${row.reference},${row.quantity},${row.week},"${row.explanation}",${row.status}`);
    });
    lines.push('');
    lines.push('WEEKLY COMPARISON');
    lines.push(`Week,Cin7 OUT,${externalLabel} Ship,Variance,Status`);
    report.weeklyComparison.forEach(w => {
      lines.push(`${w.weekLabel},${w.cin7Total},${w.wccsTotal},${w.variance},${w.status}`);
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-reconciliation-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    const id = report?.reportId || multiReport?.reportId;
    if (!id) return;
    setExportOpen(false);
    try {
      // window.open cannot carry the Authorization header — fetch with auth
      // and download the blob (same pattern as the CSV export above)
      const res = await fetch(`${API_BASE}/data-validation/export-pdf/${id}`, {
        headers: { ...authHeaders }
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `PDF export failed (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconciliation-${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err?.message || 'PDF export failed');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      {/* Header */}
      <header className="border-b border-neutral-800 bg-neutral-900 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-base font-semibold">Inventory Reconciliation</h1>
              <p className="text-xs text-neutral-500">Cin7 vs {externalLabel} Variance Detection</p>
            </div>
          </div>
          {phase === 'report' && (
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => setExportOpen(!exportOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-neutral-300 hover:bg-neutral-800 rounded border border-neutral-700"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export
                  <ChevronDown className="w-3 h-3" />
                </button>
                {exportOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-neutral-800 border border-neutral-700 rounded shadow-lg z-20 min-w-[140px]">
                    <button
                      onClick={() => { handleExport(); setExportOpen(false); }}
                      className="w-full text-left px-3 py-2 text-xs text-neutral-300 hover:bg-neutral-700"
                    >
                      Export CSV
                    </button>
                    <button
                      onClick={handleExportPdf}
                      className="w-full text-left px-3 py-2 text-xs text-neutral-300 hover:bg-neutral-700"
                    >
                      Export PDF
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-neutral-300 hover:bg-neutral-800 rounded"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                New
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Upload Phase */}
        {phase === 'upload' && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-lg font-semibold mb-1">Upload Movement Reports</h2>
              <p className="text-sm text-neutral-500">Compare Cin7 OUT vs Warehouse Shipments</p>
            </div>

              {/* Mode Toggle */}
              <div className="flex justify-center mb-6">
                <div className="inline-flex rounded border border-neutral-700 overflow-hidden">
                  <button
                    onClick={() => setMode('single')}
                    className={`px-4 py-2 text-sm ${
                      mode === 'single'
                        ? 'bg-neutral-700 text-white'
                        : 'bg-neutral-900 text-neutral-400 hover:bg-neutral-800'
                    }`}
                  >
                    Single
                  </button>
                  <button
                    onClick={() => setMode('multi-file')}
                    className={`px-4 py-2 text-sm ${
                      mode === 'multi-file'
                        ? 'bg-neutral-700 text-white'
                        : 'bg-neutral-900 text-neutral-400 hover:bg-neutral-800'
                    }`}
                  >
                    Multi-File
                  </button>
                  <button
                    disabled
                    className="px-4 py-2 text-sm bg-neutral-900 text-neutral-600 cursor-not-allowed relative"
                    title="Coming soon"
                  >
                    Multi-Warehouse
                    <span className="ml-1.5 text-[10px] bg-neutral-800 text-neutral-500 px-1.5 py-0.5 rounded-full align-middle">Soon</span>
                  </button>
                </div>
              </div>

              {/* Single Mode */}
              {mode === 'single' && (
              <>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <FileDropZone
                    label="Cin7 Export"
                    file={cin7File}
                    onFile={setCin7File}
                  />
                  <FileDropZone
                    label={`${externalLabel} Report`}
                    file={wccsFile}
                    onFile={setWccsFile}
                  />
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={() => handleReconcile(false)}
                    disabled={!cin7File || !wccsFile}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded text-sm font-medium ${
                      cin7File && wccsFile
                        ? 'bg-emerald-700 text-white hover:bg-emerald-600'
                        : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Reconcile
                  </button>
                </div>
              </>
            )}

            {/* Multi-File Mode */}
            {mode === 'multi-file' && (
              <>
                <p className="text-xs text-neutral-500 text-center mb-4">
                  Upload multiple Cin7 files and multiple warehouse files. All files will be merged and compared.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  {/* Internal Files */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Internal Files (Cin7)
                    </label>
                    <MultiFileDropZone
                      label="Drop Cin7 files"
                      files={internalFiles}
                      onFilesChange={handleInternalFilesChange}
                      onRemove={removeInternalFile}
                    />
                  </div>

                  {/* External Files */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      External Files (Warehouse)
                    </label>
                    <MultiFileDropZone
                      label="Drop warehouse files"
                      files={externalFiles}
                      onFilesChange={handleExternalFilesChange}
                      onRemove={removeExternalFile}
                    />
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={() => handleMultiFileReconcile(false)}
                    disabled={internalFiles.length === 0 || externalFiles.length === 0}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded text-sm font-medium ${
                      internalFiles.length > 0 && externalFiles.length > 0
                        ? 'bg-emerald-700 text-white hover:bg-emerald-600'
                        : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Reconcile ({internalFiles.length} + {externalFiles.length} files)
                  </button>
                </div>
              </>
            )}

            {/* Multi-Warehouse Mode */}
            {mode === 'multi-warehouse' && (
              <>
                {/* Master File */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Master File (Cin7 - All SKUs)
                  </label>
                  <FileDropZone
                    label="Cin7 Master Export"
                    file={masterFile}
                    onFile={setMasterFile}
                  />
                </div>

                {/* Warehouse Files */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-neutral-300">
                      Warehouse Files
                    </label>
                    <button
                      onClick={addWarehouseSlot}
                      className="flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-300"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Warehouse
                    </button>
                  </div>

                  <div className="space-y-3">
                    {warehouseFiles.map((wh) => (
                      <div key={wh.id} className="flex gap-3 items-start">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={wh.name}
                            onChange={(e) => updateWarehouseName(wh.id, e.target.value)}
                            placeholder="Warehouse name"
                            className="w-full mb-2 px-3 py-1.5 text-sm bg-neutral-900 border border-neutral-700 rounded text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-neutral-600"
                          />
                          <FileDropZone
                            label={`${wh.name} Report`}
                            file={wh.file}
                            onFile={(file) => updateWarehouseFile(wh.id, file)}
                            compact
                          />
                        </div>
                        {warehouseFiles.length > 1 && (
                          <button
                            onClick={() => removeWarehouseSlot(wh.id)}
                            className="p-1.5 text-neutral-500 hover:text-rose-300 mt-8"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={handleMultiReconcile}
                    disabled={!masterFile || warehouseFiles.every(wh => !wh.file)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded text-sm font-medium ${
                      masterFile && warehouseFiles.some(wh => wh.file)
                        ? 'bg-emerald-700 text-white hover:bg-emerald-600'
                        : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Reconcile All
                  </button>
                </div>
              </>
            )}

              {error && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded text-rose-300 text-sm">
                  {error}
                </div>
              )}

            {/* Recent Reports - inline below upload */}
            {recentReports.length > 0 && (
              <div className="mt-8 pt-6 border-t border-neutral-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-neutral-500" />
                    <span className="text-sm font-medium text-neutral-400">Recent Reports</span>
                  </div>
                  <button
                    onClick={fetchRecentReports}
                    disabled={recentReportsLoading}
                    className="p-1 text-neutral-500 hover:text-neutral-300 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${recentReportsLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {recentReports.slice(0, 6).map((rpt) => {
                    const isLoading = loadingReportId === rpt.report_id;
                    const variance = rpt.summary_cache?.totalVariance || 0;
                    const productName = rpt.summary_cache?.productName || rpt.summary_cache?.sku || 'Unknown';
                    const dateStr = new Date(rpt.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    });

                    return (
                      <button
                        key={rpt.report_id}
                        onClick={() => handleLoadReport(rpt.report_id)}
                        disabled={isLoading}
                        className="text-left p-3 border border-neutral-800 rounded hover:border-neutral-700 hover:bg-neutral-900/50 transition-colors disabled:opacity-50"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm text-neutral-200 truncate font-medium flex-1">
                            {productName}
                          </p>
                          {isLoading ? (
                            <Loader2 className="w-3.5 h-3.5 text-neutral-500 animate-spin flex-shrink-0" />
                          ) : (
                            <span className={`text-xs font-mono font-bold flex-shrink-0 ${
                              variance === 0 ? 'text-emerald-300' : 'text-rose-300'
                            }`}>
                              {variance > 0 ? '+' : ''}{variance.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-neutral-500">{dateStr}</span>
                          {rpt.mode === 'multi-file' && (
                            <span className="px-1 py-0.5 text-[10px] bg-neutral-800 text-neutral-500 rounded">
                              Multi
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Processing */}
        {phase === 'processing' && (
          <div className="flex flex-col items-center py-20">
            <Loader2 className="w-8 h-8 text-neutral-400 animate-spin mb-4" />
            <p className="text-neutral-400">Analyzing transactions...</p>
          </div>
        )}

        {/* Audit Phase - Hierarchical Analysis */}
        {phase === 'audit' && auditResult && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header with Score and Actions */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-neutral-200">Pre-Reconciliation Audit</h2>
                <p className="text-sm text-neutral-500">
                  Analyzing date ranges, SKU coverage, and transaction references
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-neutral-500">Quality Score</p>
                  <p className={`text-2xl font-mono font-bold ${
                    auditResult.completeness?.overallScore >= 80 ? 'text-emerald-300' :
                    auditResult.completeness?.overallScore >= 50 ? 'text-amber-200' : 'text-rose-300'
                  }`}>
                    {auditResult.completeness?.overallScore || 0}%
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleReUploadFromAudit}
                    className="px-3 py-2 text-sm border border-neutral-600 text-neutral-300 hover:bg-neutral-800 rounded"
                  >
                    Re-upload
                  </button>
                  <button
                    onClick={handleProceedFromAudit}
                    disabled={loading || !auditResult.canProceed}
                    className={`px-4 py-2 text-sm rounded font-medium ${
                      auditResult.canProceed
                        ? 'bg-emerald-700 hover:bg-emerald-600 text-white'
                        : 'bg-neutral-700 text-neutral-400 cursor-not-allowed'
                    }`}
                  >
                    {loading ? 'Processing...' : auditResult.canProceed ? 'Proceed to Reconciliation' : 'Cannot Proceed'}
                  </button>
                </div>
              </div>
            </div>

            {/* STEP 1: DATES */}
            <div className="border border-neutral-800 rounded overflow-hidden">
              <div className="bg-blue-500/10 px-4 py-3 border-b border-blue-500/20 flex items-center gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold">1</span>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-sky-300">Date Range Analysis</h3>
                  <p className="text-xs text-neutral-500">Finding the comparable time period</p>
                </div>
                {auditResult.hierarchy?.dates?.overlap?.days > 0 ? (
                  <span className="px-2 py-1 bg-emerald-500/10 text-emerald-300 text-xs rounded">
                    {auditResult.hierarchy.dates.overlap.days} days overlap
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-red-500/10 text-rose-300 text-xs rounded">No overlap</span>
                )}
              </div>
              <div className="p-4 space-y-4">
                {/* Date ranges side by side */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-neutral-900 rounded p-3">
                    <p className="text-xs text-neutral-500 mb-2">Cin7 (Internal)</p>
                    <p className="text-sm font-medium text-neutral-200">
                      {auditResult.hierarchy?.dates?.source1?.start || 'N/A'} → {auditResult.hierarchy?.dates?.source1?.end || 'N/A'}
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {auditResult.hierarchy?.dates?.source1?.days || 0} days • {auditResult.hierarchy?.dates?.source1?.transactions || 0} transactions
                    </p>
                  </div>
                  <div className="bg-neutral-900 rounded p-3">
                    <p className="text-xs text-neutral-500 mb-2">{externalLabel} (External)</p>
                    <p className="text-sm font-medium text-neutral-200">
                      {auditResult.hierarchy?.dates?.source2?.start || 'N/A'} → {auditResult.hierarchy?.dates?.source2?.end || 'N/A'}
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {auditResult.hierarchy?.dates?.source2?.days || 0} days • {auditResult.hierarchy?.dates?.source2?.transactions || 0} transactions
                    </p>
                  </div>
                </div>

                {/* Overlap highlight */}
                {auditResult.hierarchy?.dates?.overlap?.days > 0 && (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-emerald-300 font-medium uppercase tracking-wider">Comparable Period</p>
                        <p className="text-lg font-semibold text-emerald-300 mt-1">
                          {auditResult.hierarchy.dates.overlap.start} → {auditResult.hierarchy.dates.overlap.end}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-mono font-bold text-emerald-300">
                          {auditResult.hierarchy.dates.overlap.percent}%
                        </p>
                        <p className="text-xs text-neutral-500">overlap</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Outside overlap info */}
                {(auditResult.hierarchy?.dates?.outsideOverlap?.source1Only?.days > 0 ||
                  auditResult.hierarchy?.dates?.outsideOverlap?.source2Only?.days > 0) && (
                  <div className="flex gap-4 text-xs">
                    {auditResult.hierarchy?.dates?.outsideOverlap?.source1Only?.days > 0 && (
                      <p className="text-neutral-500">
                        <span className="text-sky-300">{auditResult.hierarchy.dates.outsideOverlap.source1Only.days} days</span> in Cin7 only
                      </p>
                    )}
                    {auditResult.hierarchy?.dates?.outsideOverlap?.source2Only?.days > 0 && (
                      <p className="text-neutral-500">
                        <span className="text-emerald-300">{auditResult.hierarchy.dates.outsideOverlap.source2Only.days} days</span> in {externalLabel} only
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* STEP 2: SKUs */}
            <div className="border border-neutral-800 rounded overflow-hidden">
              <div className="bg-purple-500/10 px-4 py-3 border-b border-purple-500/20 flex items-center gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-500 text-white text-xs font-bold">2</span>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-violet-300">SKU Coverage (Within Overlap)</h3>
                  <p className="text-xs text-neutral-500">Which products exist in both sources during the comparable period</p>
                </div>
                {auditResult.hierarchy?.skus?.matchingCount > 0 ? (
                  <span className="px-2 py-1 bg-emerald-500/10 text-emerald-300 text-xs rounded">
                    {auditResult.hierarchy.skus.matchingCount} SKUs matched
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-red-500/10 text-rose-300 text-xs rounded">No matches</span>
                )}
              </div>
              <div className="p-4 space-y-4">
                {/* SKU summary cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-3 text-center">
                    <p className="text-2xl font-mono font-bold text-emerald-300">
                      {auditResult.hierarchy?.skus?.matchingCount || 0}
                    </p>
                    <p className="text-xs text-neutral-400">Matching SKUs</p>
                  </div>
                  <div className="bg-neutral-900 rounded p-3 text-center">
                    <p className="text-2xl font-mono font-bold text-sky-300">
                      {auditResult.hierarchy?.skus?.source1OnlyCount || 0}
                    </p>
                    <p className="text-xs text-neutral-400">Cin7 Only</p>
                  </div>
                  <div className="bg-neutral-900 rounded p-3 text-center">
                    <p className="text-2xl font-mono font-bold text-amber-200">
                      {auditResult.hierarchy?.skus?.source2OnlyCount || 0}
                    </p>
                    <p className="text-xs text-neutral-400">{externalLabel} Only</p>
                  </div>
                </div>

                {/* Matching SKUs quantity summary */}
                {auditResult.hierarchy?.skus?.matchingCount > 0 && (
                  <div className="bg-neutral-900 rounded p-4">
                    <p className="text-xs text-neutral-500 mb-3 uppercase tracking-wider">Matching SKUs Summary</p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-mono font-bold text-sky-300">
                          {auditResult.hierarchy.skus.matchingQuantity?.source1?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-neutral-500">Cin7 OUT</p>
                      </div>
                      <div>
                        <p className="text-lg font-mono font-bold text-emerald-300">
                          {auditResult.hierarchy.skus.matchingQuantity?.source2?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-neutral-500">{externalLabel} Shipped</p>
                      </div>
                      <div>
                        <p className={`text-lg font-mono font-bold ${
                          auditResult.hierarchy.skus.matchingQuantity?.variance === 0 ? 'text-emerald-300' :
                          Math.abs(auditResult.hierarchy.skus.matchingQuantity?.variance || 0) < 100 ? 'text-amber-200' : 'text-rose-300'
                        }`}>
                          {auditResult.hierarchy.skus.matchingQuantity?.variance > 0 ? '+' : ''}
                          {auditResult.hierarchy.skus.matchingQuantity?.variance?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-neutral-500">Variance</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Top matching SKUs with variance */}
                {auditResult.hierarchy?.skus?.matching?.length > 0 && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-2">Top SKUs by Variance</p>
                    <div className="space-y-1">
                      {auditResult.hierarchy.skus.matching.slice(0, 5).map((sku: any, i: number) => (
                        <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-neutral-900/50 rounded text-sm">
                          <span className="text-neutral-300 truncate flex-1">{sku.productName || sku.sku}</span>
                          <div className="flex items-center gap-4 text-xs font-mono">
                            <span className="text-sky-300">{sku.source1Qty?.toLocaleString()}</span>
                            <span className="text-neutral-600">vs</span>
                            <span className="text-emerald-300">{sku.source2Qty?.toLocaleString()}</span>
                            <span className={`w-20 text-right ${
                              sku.variance === 0 ? 'text-emerald-300' : 'text-amber-200'
                            }`}>
                              {sku.variance > 0 ? '+' : ''}{sku.variance?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* STEP 3: References */}
            <div className="border border-neutral-800 rounded overflow-hidden">
              <div className="bg-amber-500/10 px-4 py-3 border-b border-amber-500/20 flex items-center gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold">3</span>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-amber-200">Reference Matching</h3>
                  <p className="text-xs text-neutral-500">Transaction-level comparison for matching SKUs</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${
                  auditResult.hierarchy?.references?.matchPercent >= 80 ? 'bg-emerald-500/10 text-emerald-300' :
                  auditResult.hierarchy?.references?.matchPercent >= 50 ? 'bg-amber-500/10 text-amber-200' :
                  'bg-neutral-700 text-neutral-400'
                }`}>
                  {auditResult.hierarchy?.references?.matchPercent || 0}% matched
                </span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-neutral-900 rounded p-3">
                    <p className="text-2xl font-mono font-bold text-neutral-300">
                      {auditResult.hierarchy?.references?.totalCompared || 0}
                    </p>
                    <p className="text-xs text-neutral-500">Transactions Compared</p>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-3">
                    <p className="text-2xl font-mono font-bold text-emerald-300">
                      {auditResult.hierarchy?.references?.matched || 0}
                    </p>
                    <p className="text-xs text-neutral-400">References Matched</p>
                  </div>
                  <div className="bg-neutral-900 rounded p-3">
                    <p className="text-2xl font-mono font-bold text-amber-200">
                      {(auditResult.hierarchy?.references?.unmatchedSource1 || 0) +
                       (auditResult.hierarchy?.references?.unmatchedSource2 || 0)}
                    </p>
                    <p className="text-xs text-neutral-500">Unmatched</p>
                  </div>
                </div>

                {/* Unmatched details preview */}
                {auditResult.hierarchy?.references?.unmatchedSource1Details?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-neutral-500 mb-2">Sample Unmatched from Cin7</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {auditResult.hierarchy.references.unmatchedSource1Details.slice(0, 5).map((tx: any, i: number) => (
                        <div key={i} className="flex items-center gap-3 text-xs py-1 px-2 bg-neutral-900/50 rounded">
                          <span className="text-neutral-500 w-24">{tx.date}</span>
                          <span className="text-neutral-400 truncate flex-1">{tx.productName || tx.sku}</span>
                          <span className="text-neutral-500">{tx.reference || 'no ref'}</span>
                          <span className="text-sky-300 font-mono">{tx.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Parsing Diagnostics (show if parsing issues detected) */}
            {(auditResult as any).parsingDiagnostics && (
              ((auditResult as any).parsingDiagnostics.source1?.warnings?.length > 0 ||
               (auditResult as any).parsingDiagnostics.source2?.warnings?.length > 0) && (
                <div className="border border-red-500/20 rounded overflow-hidden bg-red-500/5">
                  <div className="bg-red-500/10 px-4 py-2 border-b border-red-500/20">
                    <span className="text-xs font-medium text-rose-300 uppercase tracking-wider">
                      Column Detection Issues
                    </span>
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Source 1 issues */}
                    {(auditResult as any).parsingDiagnostics.source1?.warnings?.length > 0 && (
                      <div>
                        <p className="text-xs text-neutral-500 mb-2">Cin7 (Internal)</p>
                        <div className="space-y-1">
                          {(auditResult as any).parsingDiagnostics.source1.warnings.map((w: any, i: number) => (
                            <p key={i} className="text-sm text-rose-300">
                              {w.message}
                            </p>
                          ))}
                        </div>
                        <p className="text-xs text-neutral-500 mt-2">
                          Headers found: {(auditResult as any).parsingDiagnostics.source1.headers?.slice(0, 8).join(', ')}
                        </p>
                      </div>
                    )}
                    {/* Source 2 issues */}
                    {(auditResult as any).parsingDiagnostics.source2?.warnings?.length > 0 && (
                      <div>
                        <p className="text-xs text-neutral-500 mb-2">{externalLabel} (External)</p>
                        <div className="space-y-1">
                          {(auditResult as any).parsingDiagnostics.source2.warnings.map((w: any, i: number) => (
                            <p key={i} className="text-sm text-rose-300">
                              {w.message}
                            </p>
                          ))}
                        </div>
                        <p className="text-xs text-neutral-500 mt-2">
                          Headers found: {(auditResult as any).parsingDiagnostics.source2.headers?.slice(0, 8).join(', ')}
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-neutral-400 mt-2 pt-2 border-t border-neutral-800">
                      Check that your files have columns like "SKU", "Date", and "Quantity Out" or "Shipped"
                    </p>
                  </div>
                </div>
              )
            )}

            {/* Warnings (collapsed by default if few) */}
            {auditResult.warnings?.length > 0 && (
              <div className="border border-neutral-800 rounded overflow-hidden">
                <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                  <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Warnings ({auditResult.warnings.length})
                  </span>
                </div>
                <div className="p-4 space-y-2">
                  {auditResult.warnings.map((warning: any, i: number) => (
                    <div
                      key={i}
                      className={`flex items-start gap-3 p-2 rounded text-sm ${
                        warning.severity === 'critical' ? 'bg-red-500/10' :
                        warning.severity === 'warning' ? 'bg-amber-500/10' : 'bg-neutral-900'
                      }`}
                    >
                      <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                        warning.severity === 'critical' ? 'bg-red-500/30 text-rose-300' :
                        warning.severity === 'warning' ? 'bg-amber-500/30 text-amber-200' :
                        'bg-neutral-700 text-neutral-400'
                      }`}>
                        {warning.severity?.toUpperCase() || 'INFO'}
                      </span>
                      <p className="text-neutral-300 flex-1">{warning.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {auditResult.recommendations.length > 0 && (
              <div className="border border-neutral-800 rounded overflow-hidden">
                <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                  <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Recommendations
                  </span>
                </div>
                <div className="p-4 space-y-2">
                  {auditResult.recommendations.map((rec, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="px-2 py-0.5 text-xs font-medium bg-neutral-800 text-neutral-400 rounded">
                        P{rec.priority}
                      </span>
                      <div>
                        <p className="text-sm text-neutral-200">{rec.action}</p>
                        <p className="text-xs text-neutral-500">{rec.details}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Clarification Phase - Data Quality Questions */}
        {phase === 'clarification' && clarificationState && (
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Header */}
            <div className="text-center">
              <h2 className="text-lg font-semibold text-neutral-200">Data Quality Review</h2>
              <p className="text-sm text-neutral-500 mt-1">
                We detected issues that affect variance calculation. Please review and configure.
              </p>
            </div>

            {/* Variance Preview */}
            <div className="bg-neutral-900 border border-neutral-800 rounded p-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-xs text-neutral-500 uppercase">Raw Variance</p>
                  <p className="text-2xl font-mono font-bold text-rose-300">
                    {clarificationState.summary.rawVariance > 0 ? '+' : ''}{clarificationState.summary.rawVariance.toLocaleString()}
                  </p>
                  <p className="text-xs text-neutral-500">Before corrections</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase">Estimated True Variance</p>
                  <p className="text-2xl font-mono font-bold text-emerald-300">
                    ~{clarificationState.summary.estimatedTrueVariance.toLocaleString()}
                  </p>
                  <p className="text-xs text-neutral-500">After corrections</p>
                </div>
              </div>
            </div>

            {/* Issues Summary */}
            {clarificationState.dataIssues.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-neutral-400">Issues Detected</p>
                {clarificationState.dataIssues.map((issue, i) => (
                  <div key={i} className={`p-3 rounded border ${
                    issue.severity === 'critical' ? 'bg-red-500/10 border-red-500/20 text-rose-300' :
                    issue.severity === 'warning' ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' :
                    'bg-blue-500/10 border-blue-500/30 text-sky-300'
                  }`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase font-medium">{issue.type.replace(/_/g, ' ')}</span>
                      <span className="text-sm">{issue.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Clarification Questions */}
            <div className="space-y-4">
              <p className="text-sm font-medium text-neutral-400">Configuration</p>
              {clarificationState.clarifications.map((clarification) => (
                <div key={clarification.id} className="bg-neutral-900 border border-neutral-800 rounded p-4">
                  <p className="text-sm text-neutral-200 mb-3">{clarification.question}</p>

                  {clarification.type === 'select' && clarification.options && (
                    <div className="space-y-2">
                      {clarification.options.map((option) => (
                        <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name={clarification.id}
                            value={option.value}
                            checked={clarificationAnswers[clarification.id] === option.value ||
                              (option.value === 'custom' && clarificationAnswers[clarification.id]?.startsWith('custom:'))}
                            onChange={(e) => setClarificationAnswers(prev => ({
                              ...prev,
                              [clarification.id]: e.target.value
                            }))}
                            className="w-4 h-4 text-emerald-300 bg-neutral-800 border-neutral-600 focus:ring-emerald-500"
                          />
                          <span className={`text-sm ${option.recommended ? 'text-emerald-300' : 'text-neutral-300'}`}>
                            {option.label}
                          </span>
                        </label>
                      ))}
                      {/* Custom ratio input when 'custom' is selected */}
                      {(clarificationAnswers[clarification.id] === 'custom' ||
                        clarificationAnswers[clarification.id]?.startsWith('custom:')) && (
                        <div className="ml-7 mt-2">
                          <input
                            type="number"
                            step="0.1"
                            min="0.1"
                            placeholder="Enter ratio (e.g., 2.6)"
                            value={clarificationAnswers[clarification.id]?.replace('custom:', '') || ''}
                            onChange={(e) => setClarificationAnswers(prev => ({
                              ...prev,
                              [clarification.id]: `custom:${e.target.value}`
                            }))}
                            className="w-32 px-3 py-1.5 bg-neutral-800 border border-neutral-700 rounded text-sm text-neutral-200 focus:outline-none focus:border-emerald-700"
                          />
                          <span className="ml-2 text-xs text-neutral-500">units per case</span>
                        </div>
                      )}
                    </div>
                  )}

                  {clarification.impact && (
                    <p className="text-xs text-neutral-500 mt-3 italic">{clarification.impact}</p>
                  )}

                  {clarification.skuList && clarification.skuList.length > 0 && (
                    <div className="mt-3 p-2 bg-neutral-950 rounded text-xs">
                      <p className="text-neutral-500 mb-1">Affected SKUs:</p>
                      <div className="flex flex-wrap gap-1">
                        {clarification.skuList.slice(0, 5).map(s => (
                          <span key={s.sku} className="px-2 py-0.5 bg-neutral-800 rounded text-neutral-400">
                            {s.sku}: {s.units.toLocaleString()}
                          </span>
                        ))}
                        {clarification.skuList.length > 5 && (
                          <span className="px-2 py-0.5 text-neutral-500">
                            +{clarification.skuList.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex justify-center gap-4">
              <button
                onClick={handleReUploadFromAudit}
                className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-200"
              >
                Re-upload Files
              </button>
              <button
                onClick={handleClarificationSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 rounded text-sm font-medium bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Apply & Reconcile
              </button>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-rose-300 text-sm">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Report Phase - Single Source */}
        {phase === 'report' && report && !multiReport && (() => {
          // Derive the active report: per-SKU if selected, aggregate otherwise
          const activeReport: ReconciliationReport = selectedSku
            ? (report.skuResults?.find(r => r.sku === selectedSku) || report)
            : report;

          return (
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Main Report - 2 cols */}
            <div className="lg:col-span-2 space-y-4">

              {/* Per-SKU Summary Table (multi-SKU only) */}
              {report.skuBreakdown && report.skuBreakdown.length > 1 && (
                <div className="border border-neutral-800 rounded overflow-hidden">
                  <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">Per-SKU Breakdown</span>
                      <span className="px-1.5 py-0.5 text-[10px] bg-amber-500/10 text-amber-200 rounded">{report.skuBreakdown.length} SKUs</span>
                    </div>
                    {selectedSku && (
                      <button
                        onClick={() => setSelectedSku(null)}
                        className="text-xs text-neutral-400 hover:text-neutral-200 px-2 py-1 bg-neutral-800 rounded"
                      >
                        View All
                      </button>
                    )}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-neutral-900/50 text-neutral-500 text-xs">
                        <th className="px-3 py-2 text-left font-medium">SKU</th>
                        <th className="px-3 py-2 text-left font-medium">Product</th>
                        <th className="px-3 py-2 text-right font-medium">Cin7 OUT</th>
                        <th className="px-3 py-2 text-right font-medium">{externalLabel}</th>
                        <th className="px-3 py-2 text-right font-medium">Variance</th>
                        <th className="px-3 py-2 text-center font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.skuBreakdown.map((item) => (
                        <tr
                          key={item.sku}
                          className={`border-t border-neutral-800 cursor-pointer hover:bg-neutral-800/30 ${
                            selectedSku === item.sku ? 'bg-blue-500/10 border-l-2 border-l-blue-500' : ''
                          }`}
                          onClick={() => setSelectedSku(selectedSku === item.sku ? null : item.sku)}
                        >
                          <td className="px-3 py-2 font-mono text-xs text-neutral-300">{item.sku}</td>
                          <td className="px-3 py-2 text-neutral-200 text-xs">{item.productName}</td>
                          <td className="px-3 py-2 text-right font-mono text-neutral-300">{item.cin7TotalOut.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono text-neutral-300">{item.externalTotalShipped.toLocaleString()}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${item.variance !== 0 ? 'text-rose-300' : 'text-emerald-300'}`}>
                            {item.variance > 0 ? '+' : ''}{item.variance.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`text-xs ${selectedSku === item.sku ? 'text-sky-300' : 'text-neutral-600'}`}>
                              {selectedSku === item.sku ? 'Viewing' : 'View'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-neutral-700 bg-neutral-900/50">
                        <td className="px-3 py-2 font-mono text-xs text-neutral-500" colSpan={2}>TOTAL EXPOSURE</td>
                        <td className="px-3 py-2 text-right font-mono text-neutral-400">{report.summary.cin7TotalOut.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono text-neutral-400">{report.summary.wccsTotalShipped.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-rose-300">{report.summary.totalAbsoluteVariance?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-center text-[10px] text-neutral-600">absolute</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Location Filter Banner */}
              {activeReport.appliedConfig?.locationFiltered && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-sm text-blue-300">
                    Filtered to <strong>{activeReport.appliedConfig.locationFiltered}</strong>.
                    {activeReport.appliedConfig.locationOriginalCount && (
                      <> Original file had {activeReport.appliedConfig.locationOriginalCount.toLocaleString()} transactions across multiple locations.</>
                    )}
                  </p>
                </div>
              )}

              {/* Summary Row */}
              <div className="border border-neutral-800 rounded overflow-hidden">
                <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                  <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    {selectedSku
                      ? `${activeReport.summary.productName || activeReport.summary.sku || selectedSku}`
                      : `${report.summary.productName || 'Inventory'} (${report.summary.sku || 'All SKUs'})`
                    }
                  </span>
                  <span className="text-xs text-neutral-600 ml-2">
                    {activeReport.summary.dateRange.start} - {activeReport.summary.dateRange.end}
                  </span>
                  {selectedSku && (
                    <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-blue-500/10 text-sky-300 rounded">
                      SKU {selectedSku}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 divide-x divide-neutral-800">
                  <div className="px-4 py-3 text-center">
                    <p className="text-xs text-neutral-500 mb-1">Cin7 OUT</p>
                    <p className="text-xl font-mono font-bold">{activeReport.summary.cin7TotalOut.toLocaleString()}</p>
                    <p className="text-xs text-neutral-600">{activeReport.summary.transactionCounts.cin7} txns</p>
                  </div>
                  <div className="px-4 py-3 text-center">
                    <p className="text-xs text-neutral-500 mb-1">{externalLabel} Shipped</p>
                    <p className="text-xl font-mono font-bold">{activeReport.summary.wccsTotalShipped.toLocaleString()}</p>
                    <p className="text-xs text-neutral-600">{activeReport.summary.transactionCounts.wccs} txns</p>
                  </div>
                  {/* Variance card: show absolute total for aggregate multi-SKU, directional for single/per-SKU */}
                  {!selectedSku && report.summary.totalAbsoluteVariance != null ? (
                    <div className={`px-4 py-3 text-center ${report.summary.totalAbsoluteVariance !== 0 ? 'bg-red-500/5' : 'bg-emerald-500/5'}`}>
                      <p className="text-xs text-neutral-500 mb-1">VARIANCE</p>
                      <p className={`text-xl font-mono font-bold ${report.summary.totalAbsoluteVariance !== 0 ? 'text-rose-300' : 'text-emerald-300'}`}>
                        {report.summary.totalAbsoluteVariance.toLocaleString()}
                      </p>
                      <p className="text-xs text-neutral-500">{report.skuBreakdown?.length} SKUs · total exposure</p>
                    </div>
                  ) : (
                    <div className={`px-4 py-3 text-center ${activeReport.summary.totalVariance !== 0 ? 'bg-red-500/5' : 'bg-emerald-500/5'}`}>
                      <p className="text-xs text-neutral-500 mb-1">VARIANCE</p>
                      <p className={`text-xl font-mono font-bold ${activeReport.summary.totalVariance !== 0 ? 'text-rose-300' : 'text-emerald-300'}`}>
                        {activeReport.summary.totalVariance > 0 ? '+' : ''}{activeReport.summary.totalVariance.toLocaleString()}
                      </p>
                      <p className="text-xs text-neutral-500">{activeReport.summary.varianceDirection}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Flagged Transactions */}
              {(() => {
                const allFindings = [...activeReport.flaggedTransactions, ...userFindings];
                // Only count accepted findings toward explained variance
                const acceptedFindings = allFindings.filter((row, i) => {
                  const key = getFindingKey(row, i);
                  const status = findingStatuses[key];
                  // Known/validated findings default to accepted, auto-detected default to pending
                  if (status === 'accepted') return true;
                  if (status === 'ignored') return false;
                  // Default: known = accepted, auto = pending
                  return row.source === 'known' || row.status === 'VALIDATED';
                });
                const totalExplained = acceptedFindings.reduce((s, r) => s + r.quantity, 0);
                const pendingCount = allFindings.filter((row, i) => {
                  const key = getFindingKey(row, i);
                  return !findingStatuses[key] && row.source === 'auto';
                }).length;

                return (
                  <div className="border border-neutral-800 rounded overflow-hidden">
                    <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                          Flagged Transactions
                        </span>
                        <button
                          onClick={() => setShowAddForm(!showAddForm)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-neutral-800 hover:bg-neutral-700 rounded text-neutral-300 transition-colors"
                        >
                          <Plus size={12} />
                          Add
                        </button>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        {pendingCount > 0 && (
                          <span className="text-amber-200">{pendingCount} pending review</span>
                        )}
                        <span className="text-neutral-600">
                          Accepted: {totalExplained.toLocaleString()} units
                        </span>
                      </div>
                    </div>

                    {/* Add Finding Form */}
                    {showAddForm && (
                      <div className="bg-neutral-900/50 border-b border-neutral-800 p-4">
                        <div className="grid grid-cols-5 gap-3">
                          <input
                            type="text"
                            placeholder="Transaction (SO-####)"
                            value={newFinding.transaction}
                            onChange={e => setNewFinding(p => ({ ...p, transaction: e.target.value }))}
                            className="px-2 py-1.5 text-sm bg-neutral-800 border border-neutral-700 rounded text-neutral-200 placeholder-neutral-500 focus:outline-none focus:border-neutral-600"
                          />
                          <input
                            type="text"
                            placeholder="Reference"
                            value={newFinding.reference}
                            onChange={e => setNewFinding(p => ({ ...p, reference: e.target.value }))}
                            className="px-2 py-1.5 text-sm bg-neutral-800 border border-neutral-700 rounded text-neutral-200 placeholder-neutral-500 focus:outline-none focus:border-neutral-600"
                          />
                          <input
                            type="number"
                            placeholder="Qty Gap"
                            value={newFinding.quantity}
                            onChange={e => setNewFinding(p => ({ ...p, quantity: e.target.value }))}
                            className="px-2 py-1.5 text-sm bg-neutral-800 border border-neutral-700 rounded text-neutral-200 placeholder-neutral-500 focus:outline-none focus:border-neutral-600"
                          />
                          <input
                            type="text"
                            placeholder="Week (e.g., Aug 10 - 16)"
                            value={newFinding.week}
                            onChange={e => setNewFinding(p => ({ ...p, week: e.target.value }))}
                            className="px-2 py-1.5 text-sm bg-neutral-800 border border-neutral-700 rounded text-neutral-200 placeholder-neutral-500 focus:outline-none focus:border-neutral-600"
                          />
                          <input
                            type="text"
                            placeholder="Explanation"
                            value={newFinding.explanation}
                            onChange={e => setNewFinding(p => ({ ...p, explanation: e.target.value }))}
                            className="px-2 py-1.5 text-sm bg-neutral-800 border border-neutral-700 rounded text-neutral-200 placeholder-neutral-500 focus:outline-none focus:border-neutral-600"
                          />
                        </div>
                        <div className="flex justify-end gap-2 mt-3">
                          <button
                            onClick={() => setShowAddForm(false)}
                            className="px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-200 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAddFinding}
                            disabled={!newFinding.quantity || (!newFinding.reference && !newFinding.transaction)}
                            className="px-3 py-1.5 text-xs bg-neutral-700 hover:bg-neutral-600 disabled:bg-neutral-800 disabled:text-neutral-600 rounded text-neutral-200 transition-colors"
                          >
                            Add Finding
                          </button>
                        </div>
                      </div>
                    )}

                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-900/50 text-neutral-500 text-xs">
                          <th className="px-3 py-2 text-left font-medium">Transaction</th>
                          <th className="px-3 py-2 text-left font-medium">Reference</th>
                          <th className="px-3 py-2 text-right font-medium">Qty Gap</th>
                          <th className="px-3 py-2 text-left font-medium">Week</th>
                          <th className="px-3 py-2 text-left font-medium">Source</th>
                          <th className="px-3 py-2 text-center font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allFindings.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-3 py-6 text-center text-neutral-500 text-sm">
                              No findings yet. Click "Add" to flag a transaction.
                            </td>
                          </tr>
                        ) : (
                          allFindings.map((row, i) => {
                            const key = getFindingKey(row, i);
                            const status = findingStatuses[key];
                            // Default status based on source
                            const effectiveStatus = status || (row.source === 'known' || row.status === 'VALIDATED' ? 'accepted' : 'pending');
                            const isIgnored = effectiveStatus === 'ignored';

                            return (
                              <tr
                                key={key}
                                className={`border-t border-neutral-800 hover:bg-neutral-900/30 ${isIgnored ? 'opacity-40' : ''}`}
                              >
                                <td className="px-3 py-2 font-mono text-neutral-300">{row.transaction}</td>
                                <td className="px-3 py-2 font-mono text-neutral-400">{row.reference}</td>
                                <td className={`px-3 py-2 text-right font-mono font-bold ${isIgnored ? 'text-neutral-500 line-through' : 'text-rose-300'}`}>
                                  {row.quantity}
                                </td>
                                <td className="px-3 py-2 text-neutral-400 text-xs">{row.week}</td>
                                <td className="px-3 py-2">
                                  <span className="px-1.5 py-0.5 text-xs rounded border border-neutral-700/80 bg-neutral-800/60 text-neutral-400">
                                    {row.source === 'known' ? 'KNOWN' : row.source === 'auto' ? 'AUTO' : row.source === 'reference' ? 'REF' : 'USER'}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center justify-center gap-1">
                                    {effectiveStatus === 'accepted' ? (
                                      <>
                                        <span className="px-1.5 py-0.5 text-xs rounded bg-emerald-500/10 text-emerald-300">
                                          ACCEPTED
                                        </span>
                                        <button
                                          onClick={() => handleSetFindingStatus(key, 'ignored')}
                                          className="ml-1 px-1.5 py-0.5 text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
                                          title="Ignore this finding"
                                        >
                                          ignore
                                        </button>
                                      </>
                                    ) : effectiveStatus === 'ignored' ? (
                                      <button
                                        onClick={() => handleSetFindingStatus(key, 'accepted')}
                                        className="px-1.5 py-0.5 text-xs text-neutral-500 hover:text-emerald-300 transition-colors"
                                      >
                                        restore
                                      </button>
                                    ) : (
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => handleSetFindingStatus(key, 'accepted')}
                                          className="px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/30 rounded transition-colors"
                                        >
                                          Accept
                                        </button>
                                        <button
                                          onClick={() => handleSetFindingStatus(key, 'ignored')}
                                          className="px-2 py-0.5 text-xs bg-neutral-700/50 text-neutral-400 hover:bg-neutral-600/50 rounded transition-colors"
                                        >
                                          Ignore
                                        </button>
                                      </div>
                                    )}
                                    {row.source === 'user' && row.id && (
                                      <button
                                        onClick={() => handleDeleteFinding(row.id!)}
                                        className="ml-2 p-1 text-neutral-500 hover:text-rose-300 transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Reference Breakdown (PRIMARY - matches by reference number first) */}
              {activeReport.referenceMatching && activeReport.referenceMatching.matches.length > 0 && (
                <div className="border border-neutral-800 rounded overflow-hidden">
                  <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                          Reference Breakdown
                        </span>
                        <span className="px-1.5 py-0.5 text-[10px] bg-emerald-500/10 text-emerald-300 rounded">PRIMARY</span>
                      </div>
                      <p className="text-xs text-neutral-600 mt-0.5">
                        Matches by document reference • {activeReport.referenceMatching.summary.totalReferences} refs tracked
                      </p>
                    </div>
                    <select
                      value={refFilter}
                      onChange={(e) => setRefFilter(e.target.value)}
                      className="bg-neutral-800 text-neutral-300 text-xs px-2 py-1 rounded border border-neutral-700"
                    >
                      <option value="with_variance">With Variance</option>
                      <option value="matched">Matched ✓</option>
                      {((activeReport.referenceMatching.summary.probableMatchCount ?? 0) > 0) && (
                        <option value="probable_match">Probable Match</option>
                      )}
                      <option value="cin7_only">Cin7 Only</option>
                      <option value="external_only">{externalLabel} Only</option>
                      <option value="timing_lag">Timing Lag</option>
                      <option value="all">All References</option>
                    </select>
                  </div>

                  {/* Summary Cards — data-driven */}
                  {(() => {
                    const s = activeReport.referenceMatching.summary;
                    const cards: Array<{ value: number; label: string; color: string }> = [
                      { value: s.matchedCount, label: 'Matched', color: 'text-emerald-300' },
                      { value: s.qtyMismatchCount, label: 'Qty Mismatch', color: 'text-amber-200' },
                    ];
                    if ((s.probableMatchCount ?? 0) > 0) {
                      cards.push({ value: s.probableMatchCount!, label: 'Probable', color: 'text-teal-300' });
                    }
                    cards.push(
                      { value: s.cin7OnlyCount, label: 'Cin7 Only', color: 'text-sky-300' },
                      { value: s.externalOnlyCount, label: `${externalLabel} Only`, color: 'text-violet-300' },
                    );
                    return (
                      <div className="grid gap-2 p-3 bg-neutral-900/30" style={{ gridTemplateColumns: `repeat(${cards.length + 1}, minmax(0, 1fr))` }}>
                        {cards.map((c, i) => (
                          <div key={i} className="bg-neutral-800/50 rounded p-2 text-center">
                            <div className={`text-lg font-mono ${c.color}`}>{c.value}</div>
                            <div className="text-xs text-neutral-500">{c.label}</div>
                          </div>
                        ))}
                        <div className="bg-neutral-800/50 rounded p-2 text-center">
                          <div className={`text-lg font-mono ${s.netVariance === 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                            {s.netVariance > 0 ? '+' : ''}{s.netVariance.toLocaleString()}
                          </div>
                          <div className="text-xs text-neutral-500">Net Variance</div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Reference Table */}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-neutral-900/50 text-neutral-500 text-xs">
                        <th className="px-3 py-2 text-left font-medium">Reference</th>
                        <th className="px-3 py-2 text-right font-medium">Cin7 Qty</th>
                        <th className="px-3 py-2 text-left font-medium">Cin7 Week</th>
                        <th className="px-3 py-2 text-right font-medium">{externalLabel} Qty</th>
                        <th className="px-3 py-2 text-left font-medium">{externalLabel} Week</th>
                        <th className="px-3 py-2 text-right font-medium">Variance</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Data-driven status config — neutral chip + small status dot.
                        // The label always sits beside the dot, so color is never
                        // the only signal. Add new statuses here, nothing else changes.
                        const STATUS_CONFIG: Record<string, { dot: string; label: string }> = {
                          MATCHED:            { dot: 'bg-emerald-300/80', label: 'MATCHED' },
                          QUANTITY_MISMATCH:  { dot: 'bg-amber-300/80',   label: 'QTY MISMATCH' },
                          PROBABLE_MATCH:     { dot: 'bg-teal-300/70',    label: 'PROBABLE' },
                          CIN7_ONLY:          { dot: 'bg-sky-300/70',     label: 'CIN7 ONLY' },
                          EXTERNAL_ONLY:      { dot: 'bg-violet-300/70',  label: `${externalLabel.toUpperCase()} ONLY` },
                          FLOW_THROUGH:       { dot: 'bg-neutral-400',    label: 'FLOW-THROUGH' },
                          DATE_GAP:           { dot: 'bg-neutral-500',    label: 'DATE GAP' },
                        };
                        const DEFAULT_CFG = { dot: 'bg-neutral-500', label: '' };

                        return activeReport.referenceMatching.matches
                          .filter((match: any) => {
                            if (refFilter === 'all') return true;
                            if (refFilter === 'matched') return match.matchStatus === 'MATCHED';
                            if (refFilter === 'with_variance') return match.variance !== 0;
                            if (refFilter === 'probable_match') return match.matchStatus === 'PROBABLE_MATCH';
                            if (refFilter === 'cin7_only') return match.matchStatus === 'CIN7_ONLY';
                            if (refFilter === 'external_only') return match.matchStatus === 'EXTERNAL_ONLY';
                            if (refFilter === 'timing_lag') return match.timingGap && Math.abs(match.timingGap.daysDifference) > 0;
                            return true;
                          })
                          .slice(0, 50)
                          .map((match: any, i: number) => {
                            const cfg = STATUS_CONFIG[match.matchStatus] || DEFAULT_CFG;
                            return (
                              <tr key={i} className="border-t border-neutral-800">
                                <td className="px-3 py-2 text-neutral-200 font-mono text-xs">
                                  {match.reference}
                                  {match.matchNote && (
                                    <div className="text-[10px] text-neutral-400 mt-0.5">{match.matchNote}</div>
                                  )}
                                  {match.cin7Warehouses?.length > 0 && match.cin7Warehouses.some(
                                    (w: string) => !w.toLowerCase().includes(externalLabel.toLowerCase())
                                  ) && (
                                    <div className="text-[10px] text-amber-200/70 mt-0.5">
                                      {match.cin7Warehouses.filter((w: string) => !w.toLowerCase().includes(externalLabel.toLowerCase())).join(', ')}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-neutral-300">
                                  {match.cin7Qty !== null ? match.cin7Qty.toLocaleString() : '-'}
                                </td>
                                <td className="px-3 py-2 text-neutral-400 text-xs">{match.cin7Week || '-'}</td>
                                <td className="px-3 py-2 text-right font-mono text-neutral-300">
                                  {match.externalQty !== null ? match.externalQty.toLocaleString() : '-'}
                                </td>
                                <td className="px-3 py-2 text-neutral-400 text-xs">
                                  {match.externalWeek || '-'}
                                  {match.timingGap && Math.abs(match.timingGap.daysDifference) > 0 && (
                                    <span className="ml-1 px-1 py-0.5 text-xs bg-neutral-800 text-neutral-400 rounded">
                                      {match.timingGap.daysDifference > 0 ? '+' : ''}{match.timingGap.daysDifference}d
                                    </span>
                                  )}
                                </td>
                                <td className={`px-3 py-2 text-right font-mono font-bold ${
                                  match.variance === 0 ? 'text-emerald-300' : 'text-rose-300'
                                }`}>
                                  {match.variance > 0 ? '+' : ''}{match.variance.toLocaleString()}
                                </td>
                                <td className="px-3 py-2">
                                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded border border-neutral-700/80 bg-neutral-800/60 text-neutral-300 whitespace-nowrap">
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
                                    {cfg.label || match.matchStatus}
                                  </span>
                                </td>
                              </tr>
                            );
                          });
                      })()}
                    </tbody>
                  </table>
                  {activeReport.referenceMatching.matches.length > 50 && (
                    <div className="px-3 py-2 text-xs text-neutral-500 border-t border-neutral-800">
                      Showing 50 of {activeReport.referenceMatching.matches.length} references
                    </div>
                  )}
                </div>
              )}

              {/* Weekly Breakdown (FALLBACK - aggregate view) */}
              <div className="border border-neutral-800 rounded overflow-hidden">
                <div
                  className="bg-neutral-900 px-4 py-2 border-b border-neutral-800 flex items-center justify-between cursor-pointer hover:bg-neutral-800/50"
                  onClick={() => setWeeklyCollapsed(!weeklyCollapsed)}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-600">
                        {weeklyCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </span>
                      <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                        Weekly Breakdown
                      </span>
                      <span className="px-1.5 py-0.5 text-[10px] bg-neutral-700 text-neutral-400 rounded">AGGREGATE</span>
                    </div>
                    <p className="text-xs text-neutral-600 mt-0.5 ml-5">
                      Totals by week • {activeReport.weeklyComparison.filter(w => Math.abs(w.variance) > 10).length} weeks with variance
                    </p>
                  </div>
                </div>
                {!weeklyCollapsed && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-neutral-900/50 text-neutral-500 text-xs">
                        <th className="px-3 py-2 text-left font-medium w-6"></th>
                        <th className="px-3 py-2 text-left font-medium">Week</th>
                        <th className="px-3 py-2 text-right font-medium">Cin7 OUT</th>
                        <th className="px-3 py-2 text-right font-medium">{externalLabel} Ship</th>
                        <th className="px-3 py-2 text-right font-medium">Variance</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeReport.weeklyComparison.map((week) => (
                      <React.Fragment key={week.weekKey}>
                        <tr
                          className={`border-t border-neutral-800 cursor-pointer hover:bg-neutral-900/30 ${
                            Math.abs(week.variance) > 50 ? 'bg-red-500/5' : ''
                          }`}
                          onClick={() => toggleWeek(week.weekKey)}
                        >
                          <td className="px-3 py-2 text-neutral-600">
                            {expandedWeeks.has(week.weekKey)
                              ? <ChevronDown className="w-3.5 h-3.5" />
                              : <ChevronRight className="w-3.5 h-3.5" />
                            }
                          </td>
                          <td className="px-3 py-2 text-neutral-200">{week.weekLabel}</td>
                          <td className="px-3 py-2 text-right font-mono text-neutral-300">{week.cin7Total.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono text-neutral-300">{week.wccsTotal.toLocaleString()}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${
                            week.variance === 0 ? 'text-emerald-300' : 'text-rose-300'
                          }`}>
                            {week.variance > 0 ? '+' : ''}{week.variance.toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            {week.status === 'INVESTIGATE' ? (
                              <span className="px-1.5 py-0.5 text-xs bg-red-500/10 text-rose-300 rounded">
                                INVESTIGATE
                              </span>
                            ) : (
                              <span className="text-xs text-neutral-600">Balanced</span>
                            )}
                          </td>
                        </tr>

                        {/* Expanded transactions */}
                        {expandedWeeks.has(week.weekKey) && (
                          <tr className="bg-neutral-900/20">
                            <td colSpan={6} className="px-6 py-3">
                              <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                  <p className="text-neutral-500 mb-2 font-medium">Cin7 ({week.cin7Transactions.length})</p>
                                  {week.cin7Transactions.length === 0 ? (
                                    <p className="text-neutral-600 italic">No transactions</p>
                                  ) : (
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {week.cin7Transactions.map((tx, i) => (
                                        <div key={i} className="flex justify-between bg-neutral-800/50 rounded px-2 py-1">
                                          <span className="text-neutral-400 truncate">{tx.dateStr} - {tx.ref || tx.docRef || '-'}</span>
                                          <span className="font-mono text-neutral-200 ml-2">{tx.qty}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="text-neutral-500 mb-2 font-medium">{externalLabel} ({week.wccsTransactions.length})</p>
                                  {week.wccsTransactions.length === 0 ? (
                                    <p className="text-neutral-600 italic">No transactions</p>
                                  ) : (
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {week.wccsTransactions.map((tx, i) => (
                                        <div key={i} className="flex justify-between bg-neutral-800/50 rounded px-2 py-1">
                                          <span className="text-neutral-400 truncate">{tx.dateStr} - {tx.ref || tx.docRef || '-'}</span>
                                          <span className="font-mono text-neutral-200 ml-2">{tx.qty}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Expected Variance (Date Gaps) */}
              {activeReport.expectedVariance && activeReport.expectedVariance.dateGapTransactions > 0 && (
                <div className="border border-amber-800/50 rounded overflow-hidden bg-amber-900/10">
                  <div className="bg-amber-900/30 px-4 py-2 border-b border-amber-800/50">
                    <span className="text-xs font-medium text-amber-200 uppercase tracking-wider">
                      Expected Variance (Date Gaps)
                    </span>
                    <span className="text-xs text-amber-500/70 ml-2">
                      {activeReport.expectedVariance.dateGapTransactions} transactions outside overlap period
                    </span>
                  </div>
                  <div className="p-3 space-y-3">
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="bg-neutral-800/50 rounded p-2">
                        <div className="text-xs text-neutral-500 mb-1">Overlap Period</div>
                        <div className="text-neutral-200">
                          {activeReport.expectedVariance.overlapPeriod
                            ? `${activeReport.expectedVariance.overlapPeriod.start} – ${activeReport.expectedVariance.overlapPeriod.end}`
                            : 'No overlap'}
                        </div>
                      </div>
                      <div className="bg-neutral-800/50 rounded p-2">
                        <div className="text-xs text-neutral-500 mb-1">Cin7 Outside Overlap</div>
                        <div className="font-mono text-sky-300">
                          {activeReport.expectedVariance.cin7OutsideOverlap.toLocaleString()} units
                        </div>
                      </div>
                      <div className="bg-neutral-800/50 rounded p-2">
                        <div className="text-xs text-neutral-500 mb-1">{externalLabel} Outside Overlap</div>
                        <div className="font-mono text-violet-300">
                          {activeReport.expectedVariance.externalOutsideOverlap.toLocaleString()} units
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm bg-amber-800/20 rounded px-3 py-2">
                      <span className="text-amber-300">
                        These {activeReport.expectedVariance.dateGapTransactions} transactions are expected variance due to date coverage differences
                      </span>
                      <span className="font-mono font-bold text-amber-200">
                        {activeReport.expectedVariance.dateGapVariance > 0 ? '+' : ''}{activeReport.expectedVariance.dateGapVariance.toLocaleString()} units
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Findings */}
              {activeReport.additionalFindings.length > 0 && (
                <div className="border border-neutral-800 rounded overflow-hidden">
                  <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                    <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                      Additional Variances Detected
                    </span>
                    <span className="text-xs text-neutral-600 ml-2">
                      Not in known findings list
                    </span>
                  </div>
                  <div className="p-3 space-y-2">
                    {activeReport.additionalFindings.map((finding, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-neutral-900/50 rounded px-3 py-2">
                        <div>
                          <span className="text-neutral-200">{finding.week}</span>
                          <span className="text-neutral-500 text-xs ml-2">{finding.possibleCause}</span>
                        </div>
                        <span className="font-mono font-bold text-rose-300">
                          {finding.variance > 0 ? '+' : ''}{finding.variance}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deep Research Analysis */}
              {report.deepAnalysis?.content && (
                <div className="border border-neutral-800 rounded overflow-hidden">
                  <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800 flex items-center justify-between">
                    <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                      Deep Analysis
                    </span>
                    {report.deepAnalysis.model && (
                      <span className="text-xs text-neutral-600">
                        {report.deepAnalysis.model}
                      </span>
                    )}
                  </div>
                  <div
                    className="p-5 prose prose-invert prose-sm max-w-none
                      prose-headings:text-neutral-100 prose-headings:font-medium prose-headings:text-sm prose-headings:uppercase prose-headings:tracking-wider prose-headings:mt-6 prose-headings:mb-3 first:prose-headings:mt-0
                      prose-p:text-neutral-300 prose-p:text-sm prose-p:leading-relaxed prose-p:my-2
                      prose-strong:text-neutral-100 prose-strong:font-semibold
                      prose-ul:my-2 prose-li:text-neutral-300 prose-li:text-sm prose-li:my-1
                      prose-table:border-collapse prose-table:w-full prose-table:text-sm prose-table:my-4
                      prose-th:bg-neutral-800 prose-th:text-neutral-300 prose-th:font-medium prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:border prose-th:border-neutral-700
                      prose-td:px-3 prose-td:py-2 prose-td:border prose-td:border-neutral-800 prose-td:text-neutral-300
                      [&_table]:rounded [&_table]:overflow-hidden
                    "
                    dangerouslySetInnerHTML={{ __html: report.deepAnalysis.content }}
                  />
                </div>
              )}

              {/* Reconciliation Summary */}
              {(() => {
                // Calculate explained based on accepted findings only
                const allFindings = [...activeReport.flaggedTransactions, ...userFindings];
                const acceptedTotal = allFindings.reduce((sum, row, i) => {
                  const key = getFindingKey(row, i);
                  const status = findingStatuses[key];
                  if (status === 'accepted') return sum + row.quantity;
                  if (status === 'ignored') return sum;
                  // Default: known/validated = accepted
                  if (row.source === 'known' || row.status === 'VALIDATED') return sum + row.quantity;
                  return sum;
                }, 0);
                const totalVariance = (!selectedSku && report.summary.totalAbsoluteVariance != null)
                  ? report.summary.totalAbsoluteVariance
                  : Math.abs(activeReport.summary.totalVariance);
                const unexplained = Math.max(0, totalVariance - acceptedTotal);
                const percentExplained = totalVariance > 0 ? Math.min(100, Math.round((acceptedTotal / totalVariance) * 100)) : 100;

                // Gross variance: shows bidirectional discrepancies
                const grossAbsolute = activeReport.summary.grossAbsoluteVariance;
                const grossPositive = activeReport.summary.grossPositiveVariance;
                const grossNegative = activeReport.summary.grossNegativeVariance;
                const netVariance = activeReport.summary.totalVariance;
                const hasBidirectional = grossAbsolute != null && grossAbsolute !== Math.abs(netVariance);

                return (
                  <div className="border border-neutral-800 rounded p-4">
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="text-neutral-400">Explained (accepted)</span>
                      <span className="font-mono text-emerald-300">{acceptedTotal.toLocaleString()} units ({percentExplained}%)</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="text-neutral-400">Unexplained</span>
                      <span className="font-mono text-rose-300">{unexplained.toLocaleString()} units</span>
                    </div>
                    <div className="h-2 bg-neutral-800 rounded overflow-hidden mb-3">
                      <div
                        className="h-full bg-emerald-700"
                        style={{ width: `${percentExplained}%` }}
                      />
                    </div>
                    {hasBidirectional && (
                      <div className="border-t border-neutral-700 pt-3 mt-1">
                        <div className="text-xs text-neutral-500 mb-2">Variance is bidirectional — net masks opposing discrepancies:</div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-neutral-400">Net variance</span>
                          <span className="font-mono text-neutral-300">{netVariance > 0 ? '+' : ''}{netVariance.toLocaleString()} units</span>
                        </div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-neutral-400">Weeks over (Cin7 &gt; 3PL)</span>
                          <span className="font-mono text-amber-200">+{(grossPositive || 0).toLocaleString()} units</span>
                        </div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-neutral-400">Weeks under (3PL &gt; Cin7)</span>
                          <span className="font-mono text-amber-200">{(grossNegative || 0).toLocaleString()} units</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-neutral-400">Gross absolute</span>
                          <span className="font-mono text-white font-medium">{(grossAbsolute || 0).toLocaleString()} units</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Chat Panel - 1 col */}
            <div className="lg:col-span-1">
              <div className="border border-neutral-800 rounded h-[calc(100vh-10rem)] flex flex-col sticky top-16">
                <div className="px-3 py-2 border-b border-neutral-800 bg-neutral-900">
                  <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">Chat with Data</span>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[90%] rounded px-3 py-2 text-sm ${
                        msg.role === 'user'
                          ? 'bg-neutral-700 text-neutral-100'
                          : 'bg-neutral-900 text-neutral-300 border border-neutral-800'
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {msg.visual && <ChatVisualRenderer visual={msg.visual} />}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-neutral-900 border border-neutral-800 rounded px-3 py-2">
                        <Loader2 className="w-4 h-4 text-neutral-500 animate-spin" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Quick prompts */}
                {messages.length <= 1 && (
                  <div className="p-2 border-t border-neutral-800">
                    <p className="text-xs text-neutral-600 mb-1.5">Suggested</p>
                    <div className="space-y-1">
                      {[
                        'Show the biggest variance weeks',
                        'What transactions are missing?',
                        'What should I investigate first?'
                      ].map((q, i) => (
                        <button
                          key={i}
                          onClick={() => setChatInput(q)}
                          className="block w-full text-left text-xs px-2 py-1.5 bg-neutral-900 hover:bg-neutral-800 rounded text-neutral-400"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input */}
                <div className="p-2 border-t border-neutral-800">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleChatSend()}
                      placeholder="Ask about the data..."
                      className="flex-1 px-3 py-2 bg-neutral-900 border border-neutral-800 rounded text-sm text-neutral-100 placeholder-neutral-600 focus:outline-none focus:border-neutral-700"
                      disabled={chatLoading}
                    />
                    <button
                      onClick={handleChatSend}
                      disabled={!chatInput.trim() || chatLoading}
                      className={`p-2 rounded ${
                        chatInput.trim() && !chatLoading
                          ? 'bg-emerald-700 text-white'
                          : 'bg-neutral-800 text-neutral-600'
                      }`}
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Report Phase - Multi-Source */}
        {phase === 'report' && multiReport && (
          <div className="space-y-4">
            {/* Tabs */}
            <div className="flex gap-1 border-b border-neutral-800 overflow-x-auto">
              <button
                onClick={() => setActiveTab('aggregated')}
                className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === 'aggregated'
                    ? 'border-emerald-700 text-emerald-300'
                    : 'border-transparent text-neutral-400 hover:text-neutral-200'
                }`}
              >
                All Warehouses
              </button>
              {multiReport.warehouseComparisons.map(wh => (
                <button
                  key={wh.warehouseId}
                  onClick={() => setActiveTab(wh.warehouseId)}
                  className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === wh.warehouseId
                      ? 'border-emerald-700 text-emerald-300'
                      : 'border-transparent text-neutral-400 hover:text-neutral-200'
                  }`}
                >
                  {wh.warehouseName}
                  {wh.summary.variance !== 0 && (
                    <span className={`ml-2 text-xs ${wh.summary.variance > 0 ? 'text-rose-300' : 'text-amber-200'}`}>
                      {wh.summary.variance > 0 ? '+' : ''}{wh.summary.variance.toLocaleString()}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              {/* Main Content - 2 cols */}
              <div className="lg:col-span-2 space-y-4">
                {/* Aggregated View */}
                {activeTab === 'aggregated' && (
                  <>
                    {/* Summary */}
                    <div className="border border-neutral-800 rounded overflow-hidden">
                      <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                        <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                          Aggregated Summary
                        </span>
                        <span className="text-xs text-neutral-600 ml-2">
                          {multiReport.master.dateRange.start} - {multiReport.master.dateRange.end}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 divide-x divide-neutral-800">
                        <div className="px-4 py-3 text-center">
                          <p className="text-xs text-neutral-500 mb-1">Cin7 OUT</p>
                          <p className="text-xl font-mono font-bold">{multiReport.aggregated.totalCin7Out.toLocaleString()}</p>
                          <p className="text-xs text-neutral-600">{multiReport.master.transactionCount} txns</p>
                        </div>
                        <div className="px-4 py-3 text-center">
                          <p className="text-xs text-neutral-500 mb-1">All Warehouses</p>
                          <p className="text-xl font-mono font-bold">{multiReport.aggregated.totalWarehouseShipped.toLocaleString()}</p>
                          <p className="text-xs text-neutral-600">{multiReport.aggregated.warehouseCount} warehouses</p>
                        </div>
                        <div className="px-4 py-3 text-center">
                          <p className="text-xs text-neutral-500 mb-1">Variance</p>
                          <p className={`text-xl font-mono font-bold ${
                            multiReport.aggregated.totalVariance === 0 ? 'text-emerald-300' : 'text-rose-300'
                          }`}>
                            {multiReport.aggregated.totalVariance > 0 ? '+' : ''}{multiReport.aggregated.totalVariance.toLocaleString()}
                          </p>
                          <p className="text-xs text-neutral-600">{multiReport.aggregated.varianceDirection}</p>
                        </div>
                        <div className="px-4 py-3 text-center">
                          <p className="text-xs text-neutral-500 mb-1">SKUs</p>
                          <p className="text-xl font-mono font-bold">{multiReport.master.skuCount}</p>
                          <p className="text-xs text-neutral-600">analyzed</p>
                        </div>
                      </div>
                    </div>

                    {/* Variance Breakdown Card - shows reconcilable vs missing coverage */}
                    {multiReport.aggregated.varianceBreakdown && multiReport.aggregated.totalVariance !== 0 && (
                      <div className="border border-amber-500/30 bg-amber-500/5 rounded overflow-hidden">
                        <div className="bg-amber-500/10 px-4 py-2 border-b border-amber-500/20">
                          <span className="text-xs font-medium text-amber-200 uppercase tracking-wider">
                            Variance Breakdown
                          </span>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-2 gap-4">
                            {/* Reconcilable Variance */}
                            <div className="bg-neutral-900 rounded p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                                <span className="text-xs text-neutral-400">Reconcilable Variance</span>
                              </div>
                              <p className="text-2xl font-mono font-bold text-emerald-300">
                                {multiReport.aggregated.varianceBreakdown.reconcilable > 0 ? '+' : ''}
                                {multiReport.aggregated.varianceBreakdown.reconcilable.toLocaleString()}
                              </p>
                              <p className="text-xs text-neutral-500 mt-1">
                                {multiReport.aggregated.varianceBreakdown.trackedSkuCount} SKU{multiReport.aggregated.varianceBreakdown.trackedSkuCount !== 1 ? 's' : ''} tracked by warehouses
                              </p>
                              <p className="text-xs text-neutral-600 mt-2">
                                ← Investigate these discrepancies
                              </p>
                            </div>

                            {/* Missing Coverage */}
                            <div className="bg-neutral-900 rounded p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="w-2 h-2 rounded-full bg-amber-600"></span>
                                <span className="text-xs text-neutral-400">Missing Coverage</span>
                              </div>
                              <p className="text-2xl font-mono font-bold text-amber-200">
                                +{multiReport.aggregated.varianceBreakdown.missingCoverage.toLocaleString()}
                              </p>
                              <p className="text-xs text-neutral-500 mt-1">
                                {multiReport.aggregated.varianceBreakdown.missingSkuCount} SKU{multiReport.aggregated.varianceBreakdown.missingSkuCount !== 1 ? 's' : ''} only in Cin7
                              </p>
                              <p className="text-xs text-neutral-600 mt-2">
                                ← Add warehouse data to track
                              </p>
                            </div>
                          </div>

                          {/* Visual bar showing breakdown */}
                          <div className="mt-4">
                            <div className="flex h-3 rounded overflow-hidden bg-neutral-800">
                              {multiReport.aggregated.varianceBreakdown.reconcilable !== 0 && (
                                <div
                                  className="bg-emerald-600"
                                  style={{
                                    width: `${Math.abs(multiReport.aggregated.varianceBreakdown.reconcilable) / Math.abs(multiReport.aggregated.varianceBreakdown.total) * 100}%`
                                  }}
                                  title={`Reconcilable: ${multiReport.aggregated.varianceBreakdown.reconcilable.toLocaleString()}`}
                                />
                              )}
                              {multiReport.aggregated.varianceBreakdown.missingCoverage > 0 && (
                                <div
                                  className="bg-amber-600"
                                  style={{
                                    width: `${multiReport.aggregated.varianceBreakdown.missingCoverage / Math.abs(multiReport.aggregated.varianceBreakdown.total) * 100}%`
                                  }}
                                  title={`Missing coverage: ${multiReport.aggregated.varianceBreakdown.missingCoverage.toLocaleString()}`}
                                />
                              )}
                            </div>
                            <div className="flex justify-between mt-1 text-xs text-neutral-500">
                              <span>Reconcilable ({Math.round(Math.abs(multiReport.aggregated.varianceBreakdown.reconcilable) / Math.abs(multiReport.aggregated.varianceBreakdown.total) * 100)}%)</span>
                              <span>Missing ({Math.round(multiReport.aggregated.varianceBreakdown.missingCoverage / Math.abs(multiReport.aggregated.varianceBreakdown.total) * 100)}%)</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Warehouse Breakdown */}
                    <div className="border border-neutral-800 rounded overflow-hidden">
                      <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                        <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                          Warehouse Breakdown
                        </span>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-neutral-900/50 text-xs text-neutral-500">
                            <th className="px-4 py-2 text-left">Warehouse</th>
                            <th className="px-4 py-2 text-right">Cin7 OUT</th>
                            <th className="px-4 py-2 text-right">Shipped</th>
                            <th className="px-4 py-2 text-right">Variance</th>
                            <th className="px-4 py-2 text-right">SKUs</th>
                            <th className="px-4 py-2 text-right">Explained</th>
                          </tr>
                        </thead>
                        <tbody>
                          {multiReport.warehouseComparisons.map(wh => (
                            <tr key={wh.warehouseId} className="border-t border-neutral-800 hover:bg-neutral-900/30">
                              <td className="px-4 py-2 text-neutral-200">{wh.warehouseName}</td>
                              <td className="px-4 py-2 text-right font-mono">{wh.summary.cin7TotalOut.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right font-mono">{wh.summary.warehouseTotalShipped.toLocaleString()}</td>
                              <td className={`px-4 py-2 text-right font-mono font-medium ${
                                wh.summary.variance === 0 ? 'text-emerald-300' : 'text-rose-300'
                              }`}>
                                {wh.summary.variance > 0 ? '+' : ''}{wh.summary.variance.toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-right font-mono text-neutral-400">{wh.summary.skuCount}</td>
                              <td className="px-4 py-2 text-right font-mono text-neutral-400">{wh.reconciliation.percentExplained}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* SKU Breakdown */}
                    {multiReport.aggregated.skuBreakdown.length > 0 && (
                      <div className="border border-neutral-800 rounded overflow-hidden">
                        <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800 flex items-center justify-between">
                          <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                            SKU Breakdown (Top Variance)
                          </span>
                          <div className="flex items-center gap-4 text-xs text-neutral-500">
                            <span className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                              Tracked
                            </span>
                            <span className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-amber-600"></span>
                              Missing
                            </span>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-neutral-900/50 text-xs text-neutral-500">
                              <th className="px-4 py-2 text-left w-8"></th>
                              <th className="px-4 py-2 text-left">SKU</th>
                              <th className="px-4 py-2 text-left">Product</th>
                              <th className="px-4 py-2 text-right">Cin7</th>
                              <th className="px-4 py-2 text-right">Warehouses</th>
                              <th className="px-4 py-2 text-right">Variance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {multiReport.aggregated.skuBreakdown.slice(0, 10).map((sku, i) => (
                              <tr key={i} className={`border-t border-neutral-800 ${!sku.hasWarehouseData ? 'bg-amber-500/5' : ''}`}>
                                <td className="px-4 py-2 text-center">
                                  <span
                                    className={`inline-block w-2 h-2 rounded-full ${
                                      sku.hasWarehouseData ? 'bg-emerald-600' : 'bg-amber-600'
                                    }`}
                                    title={sku.hasWarehouseData ? 'Tracked by warehouses' : 'Missing warehouse data'}
                                  />
                                </td>
                                <td className="px-4 py-2 font-mono text-xs text-neutral-300">{sku.sku}</td>
                                <td className="px-4 py-2 text-neutral-400 truncate max-w-[200px]">{sku.productName}</td>
                                <td className="px-4 py-2 text-right font-mono">{sku.cin7Out.toLocaleString()}</td>
                                <td className="px-4 py-2 text-right font-mono">
                                  {sku.totalWarehouseShipped.toLocaleString()}
                                  {!sku.hasWarehouseData && (
                                    <span className="ml-1 text-xs text-amber-500">∅</span>
                                  )}
                                </td>
                                <td className={`px-4 py-2 text-right font-mono font-medium ${
                                  sku.variance === 0 ? 'text-emerald-300' : sku.hasWarehouseData ? 'text-rose-300' : 'text-amber-200'
                                }`}>
                                  {sku.variance > 0 ? '+' : ''}{sku.variance.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Deep Analysis */}
                    {multiReport.deepAnalysis?.content && (
                      <div className="border border-neutral-800 rounded overflow-hidden">
                        <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                          <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                            AI Analysis
                          </span>
                        </div>
                        <div
                          className="p-4 text-sm text-neutral-300 prose prose-invert prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: multiReport.deepAnalysis.content }}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Individual Warehouse View */}
                {activeTab !== 'aggregated' && (() => {
                  const wh = multiReport.warehouseComparisons.find(w => w.warehouseId === activeTab);
                  if (!wh) return null;

                  return (
                    <>
                      {/* Summary */}
                      <div className="border border-neutral-800 rounded overflow-hidden">
                        <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                          <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                            {wh.warehouseName}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 divide-x divide-neutral-800">
                          <div className="px-4 py-3 text-center">
                            <p className="text-xs text-neutral-500 mb-1">Cin7 OUT</p>
                            <p className="text-xl font-mono font-bold">{wh.summary.cin7TotalOut.toLocaleString()}</p>
                            <p className="text-xs text-neutral-600">{wh.summary.transactionCounts.cin7} txns</p>
                          </div>
                          <div className="px-4 py-3 text-center">
                            <p className="text-xs text-neutral-500 mb-1">Warehouse Shipped</p>
                            <p className="text-xl font-mono font-bold">{wh.summary.warehouseTotalShipped.toLocaleString()}</p>
                            <p className="text-xs text-neutral-600">{wh.summary.transactionCounts.warehouse} txns</p>
                          </div>
                          <div className="px-4 py-3 text-center">
                            <p className="text-xs text-neutral-500 mb-1">Variance</p>
                            <p className={`text-xl font-mono font-bold ${
                              wh.summary.variance === 0 ? 'text-emerald-300' : 'text-rose-300'
                            }`}>
                              {wh.summary.variance > 0 ? '+' : ''}{wh.summary.variance.toLocaleString()}
                            </p>
                            <p className="text-xs text-neutral-600">{wh.summary.varianceDirection}</p>
                          </div>
                        </div>
                      </div>

                      {/* Flagged Transactions */}
                      {wh.flaggedTransactions.length > 0 && (
                        <div className="border border-neutral-800 rounded overflow-hidden">
                          <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                            <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                              Flagged Transactions ({wh.flaggedTransactions.length})
                            </span>
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-neutral-900/50 text-neutral-500">
                                <th className="px-3 py-2 text-left">Transaction</th>
                                <th className="px-3 py-2 text-left">Reference</th>
                                <th className="px-3 py-2 text-right">Variance</th>
                                <th className="px-3 py-2 text-left">Week</th>
                                <th className="px-3 py-2 text-left">Explanation</th>
                              </tr>
                            </thead>
                            <tbody>
                              {wh.flaggedTransactions.map((row, i) => (
                                <tr key={i} className="border-t border-neutral-800">
                                  <td className="px-3 py-2 font-mono text-neutral-300">{row.transaction}</td>
                                  <td className="px-3 py-2 font-mono text-neutral-400">{row.reference}</td>
                                  <td className={`px-3 py-2 text-right font-mono font-medium ${
                                    row.quantity === 0 ? 'text-emerald-300' : 'text-rose-300'
                                  }`}>
                                    {row.quantity > 0 ? '+' : ''}{row.quantity.toLocaleString()}
                                  </td>
                                  <td className="px-3 py-2 text-neutral-400">{row.week}</td>
                                  <td className="px-3 py-2 text-neutral-500 truncate max-w-[200px]">{row.explanation}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Weekly Comparison */}
                      <div className="border border-neutral-800 rounded overflow-hidden">
                        <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                          <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                            Weekly Comparison
                          </span>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-neutral-900/50 text-neutral-500">
                              <th className="px-3 py-2 text-left">Week</th>
                              <th className="px-3 py-2 text-right">Cin7</th>
                              <th className="px-3 py-2 text-right">Warehouse</th>
                              <th className="px-3 py-2 text-right">Variance</th>
                              <th className="px-3 py-2 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {wh.weeklyComparison.slice(0, 15).map((week, i) => (
                              <tr key={i} className="border-t border-neutral-800">
                                <td className="px-3 py-2 text-neutral-300">{week.weekLabel}</td>
                                <td className="px-3 py-2 text-right font-mono">{week.cin7Total.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right font-mono">{week.wccsTotal.toLocaleString()}</td>
                                <td className={`px-3 py-2 text-right font-mono font-medium ${
                                  week.variance === 0 ? 'text-emerald-300' : Math.abs(week.variance) > 50 ? 'text-rose-300' : 'text-amber-200'
                                }`}>
                                  {week.variance > 0 ? '+' : ''}{week.variance.toLocaleString()}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    week.status === 'BALANCED' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-amber-500/10 text-amber-200'
                                  }`}>
                                    {week.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Chat Panel - 1 col */}
              <div className="lg:col-span-1">
                <div className="border border-neutral-800 rounded overflow-hidden sticky top-20">
                  <div className="bg-neutral-900 px-4 py-2 border-b border-neutral-800">
                    <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                      Ask Liaison
                    </span>
                  </div>
                  <div className="h-[400px] flex flex-col">
                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                      {messages.map(msg => (
                        <div key={msg.id} className={`text-sm ${msg.role === 'user' ? 'text-right' : ''}`}>
                          <span className={`inline-block px-3 py-2 rounded ${
                            msg.role === 'user'
                              ? 'bg-neutral-800 text-neutral-200'
                              : 'bg-neutral-900 text-neutral-300'
                          }`}>
                            {msg.content}
                          </span>
                          {msg.visual && <ChatVisualRenderer visual={msg.visual} />}
                        </div>
                      ))}
                      {chatLoading && (
                        <div className="text-sm">
                          <span className="inline-block px-3 py-2 rounded bg-neutral-900 text-neutral-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                          </span>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                    <div className="p-3 border-t border-neutral-800 flex gap-2">
                      <input
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleChatSend()}
                        placeholder="Ask about the data..."
                        className="flex-1 px-3 py-2 bg-neutral-900 border border-neutral-800 rounded text-sm text-neutral-100 placeholder-neutral-600 focus:outline-none focus:border-neutral-700"
                        disabled={chatLoading}
                      />
                      <button
                        onClick={handleChatSend}
                        disabled={!chatInput.trim() || chatLoading}
                        className={`p-2 rounded ${
                          chatInput.trim() && !chatLoading
                            ? 'bg-emerald-700 text-white'
                            : 'bg-neutral-800 text-neutral-600'
                        }`}
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Chat visual renderer - renders AI-generated visuals
function ChatVisualRenderer({ visual }: { visual: ChatVisual }) {
  if (!visual || !visual.type) return null;

  if (visual.type === 'data_table' && visual.columns && visual.rows) {
    return (
      <div className="mt-3 border border-neutral-700 rounded overflow-hidden">
        {visual.title && (
          <div className="px-3 py-2 bg-neutral-800 border-b border-neutral-700">
            <span className="text-xs font-medium text-neutral-300">{visual.title}</span>
          </div>
        )}
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-neutral-800/50">
              {visual.columns.map((col, i) => (
                <th key={i} className="px-3 py-1.5 text-left font-medium text-neutral-400">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visual.rows.map((row, i) => (
              <tr key={i} className="border-t border-neutral-800">
                {row.map((cell, j) => (
                  <td key={j} className={`px-3 py-1.5 ${j === 0 ? 'text-neutral-300' : 'font-mono text-neutral-100'}`}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (visual.type === 'metric_card') {
    return (
      <div className="mt-3 border border-neutral-700 rounded p-3 bg-neutral-800/50">
        {visual.title && <p className="text-xs text-neutral-400 mb-1">{visual.title}</p>}
        <p className="text-lg font-mono font-bold text-neutral-100">{visual.value}</p>
        {visual.subtitle && <p className="text-xs text-neutral-500 mt-1">{visual.subtitle}</p>}
      </div>
    );
  }

  if (visual.type === 'list_card' && visual.items) {
    return (
      <div className="mt-3 border border-neutral-700 rounded overflow-hidden">
        {visual.title && (
          <div className="px-3 py-2 bg-neutral-800 border-b border-neutral-700">
            <span className="text-xs font-medium text-neutral-300">{visual.title}</span>
          </div>
        )}
        <ul className="divide-y divide-neutral-800">
          {visual.items.map((item, i) => (
            <li key={i} className="px-3 py-2 flex justify-between text-xs">
              <span className="text-neutral-300">{item.label}</span>
              {item.value && <span className="font-mono text-neutral-100">{item.value}</span>}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Fallback: show raw JSON
  return (
    <div className="mt-2 p-2 bg-neutral-800 rounded text-xs">
      <pre className="overflow-x-auto text-neutral-400">{JSON.stringify(visual, null, 2)}</pre>
    </div>
  );
}

// File drop zone component
function FileDropZone({
  label,
  file,
  onFile,
  compact = false
}: {
  label: string;
  file: File | null;
  onFile: (f: File | null) => void;
  compact?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <label
      className={`border-2 border-dashed rounded cursor-pointer flex items-center gap-2 transition-all ${
        compact ? 'p-2 flex-row' : 'p-4 flex-col'
      } ${
        dragOver
          ? 'border-emerald-500/50 bg-emerald-500/10'
          : file
            ? 'border-emerald-600/50 bg-emerald-500/5'
            : 'border-neutral-700 hover:border-neutral-600'
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f && (f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))) {
          onFile(f);
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => onFile(e.target.files?.[0] || null)}
        className="hidden"
      />
      {file ? (
        <>
          <CheckCircle2 className={compact ? 'w-4 h-4 text-emerald-300' : 'w-5 h-5 text-emerald-300'} />
          <span className={`text-neutral-200 truncate max-w-full ${compact ? 'text-xs' : 'text-sm text-center'}`}>{file.name}</span>
        </>
      ) : (
        <>
          <FileSpreadsheet className={compact ? 'w-4 h-4 text-neutral-500' : 'w-5 h-5 text-neutral-500'} />
          <span className={compact ? 'text-xs text-neutral-400' : 'text-sm text-neutral-400'}>{label}</span>
          {!compact && <span className="text-xs text-neutral-600">Drop or click</span>}
        </>
      )}
    </label>
  );
}

// Multi-file drop zone component (for multiple files)
function MultiFileDropZone({
  label,
  files,
  onFilesChange,
  onRemove
}: {
  label: string;
  files: File[];
  onFilesChange: (files: FileList | null) => void;
  onRemove: (index: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <div className="space-y-2">
      <label
        className={`border-2 border-dashed rounded p-4 cursor-pointer flex flex-col items-center gap-2 transition-all ${
          dragOver
            ? 'border-emerald-500/50 bg-emerald-500/10'
            : files.length > 0
              ? 'border-emerald-600/50 bg-emerald-500/5'
              : 'border-neutral-700 hover:border-neutral-600'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          onFilesChange(e.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          multiple
          onChange={(e) => onFilesChange(e.target.files)}
          className="hidden"
        />
        <Plus className="w-5 h-5 text-neutral-500" />
        <span className="text-sm text-neutral-400">{label}</span>
        <span className="text-xs text-neutral-600">Drop or click (multiple)</span>
      </label>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between px-3 py-1.5 bg-neutral-900 rounded text-xs"
            >
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
                <span className="text-neutral-300 truncate">{file.name}</span>
              </div>
              <button
                onClick={() => onRemove(index)}
                className="p-1 text-neutral-500 hover:text-rose-300 flex-shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
